import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import { ItemDropdown } from '../ui/ItemDropdown'
import { DesignDropdown } from '../ui/DesignDropdown'
import { CustomerLookup } from '../ui/CustomerLookup'
import { ProspectLookup } from '../ui/ProspectLookup'
import { dataService } from '../../services/DataService'
import { useBackendAuth } from '../auth/BackendAuthProvider'
import { useTranslation } from 'react-i18next'
import { useQuotationLinesImport } from './QuotationLinesImport'
import {
  getDefaultOrderQuantity,
  getOrderQuantityErrorKey,
  validateOrderQuantity
} from '../../js/utils/OrderQuantityRules'

const getInvalidOrderQuantityLines = (lines) => lines
  .map((line, index) => ({ line, index }))
  .filter(({ line }) => line.mserp_alg_producttype !== '200000001' && line.mserp_alg_producttype !== 200000001)
  .filter(({ line }) => line._orderQuantityLoading || line._orderQuantityError || line._orderQuantitySettings)
  .map(({ line, index }) => ({
    line,
    index,
    validation: line._orderQuantitySettings
      ? validateOrderQuantity(line.mserp_requestedsalesquantity, line._orderQuantitySettings)
      : { isValid: false, reason: 'invalid' }
  }))
  .filter(({ line, validation }) => line._orderQuantityLoading || line._orderQuantityError || !validation.isValid)

// standalone: the view is the whole page (embedded /create-quotation flow) - no close button,
// the user can only work on this quotation.
// startInEditMode: open directly in edit mode (used right after creating a quotation)
export const QuotationLinesView = ({ quotationNumber, onClose, onSave, standalone = false, startInEditMode = false }) => {
  const { smmParameters, user, salutationCatalog } = useBackendAuth()
  const { t } = useTranslation(['quotation', 'common'])
  const salutationScopeKey = user?.email && smmParameters?.mserp_dataareaid
    ? `${user.email.trim().toLocaleLowerCase()}|${String(smmParameters.mserp_dataareaid).trim().toLocaleLowerCase()}`
    : null
  const activeSalutationCatalog = salutationCatalog?.scopeKey === salutationScopeKey
    ? salutationCatalog
    : null
  const salutations = activeSalutationCatalog?.data || []
  const isSalutationCatalogEnabled = activeSalutationCatalog?.enabled === true
  const [lines, setLines] = useState([])
  const [quotationDetails, setQuotationDetails] = useState(null)
  const [editedQuotationDetails, setEditedQuotationDetails] = useState(null)
  const [quotationModifiedFields, setQuotationModifiedFields] = useState(new Set())
  const [contactPerson, setContactPerson] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [warning, setWarning] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const startInEditModeAppliedRef = useRef(false) // only auto-enter edit mode on the first load
  const [editedLines, setEditedLines] = useState([])
  const [focusItemInputLineId, setFocusItemInputLineId] = useState(null)
  const [modifiedFields, setModifiedFields] = useState(new Set())
  const [failedLineIndexes, setFailedLineIndexes] = useState(new Set())
  const [selectedLines, setSelectedLines] = useState(new Set())
  const [deletedLines, setDeletedLines] = useState([])
  const [isQuotationInfoExpanded, setIsQuotationInfoExpanded] = useState(true)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [showUngroupConfirm, setShowUngroupConfirm] = useState(false)
  const [showSendToErpConfirm, setShowSendToErpConfirm] = useState(false)
  const [currentView, setCurrentView] = useState('lines') // 'lines' or 'all'
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showLinePercentModal, setShowLinePercentModal] = useState(false)
  const [bulkLinePercent, setBulkLinePercent] = useState('')
  const [applyByLineDisc, setApplyByLineDisc] = useState(false)
  const [selectedLineDisc, setSelectedLineDisc] = useState('')
  const [showFieldConflictModal, setShowFieldConflictModal] = useState(false)
  const [fieldConflictData, setFieldConflictData] = useState({ field: '', value: '', message: '' })
  const [draggedLineIndex, setDraggedLineIndex] = useState(null)
  const [dragOverLineIndex, setDragOverLineIndex] = useState(null)
  const [commercialDescriptions, setCommercialDescriptions] = useState({})
  const commercialDescriptionRequestsRef = useRef(new Map())
  const orderQuantityRequestsRef = useRef(new Map())
  const dropdownRef = useRef(null)
  const isSendingToErp = useRef(false)

  useEffect(() => {
    if (quotationNumber) {
      setCommercialDescriptions({})
      commercialDescriptionRequestsRef.current.clear()
      loadQuotationLines()
      loadQuotationDetails()
    }
  }, [quotationNumber])

  useEffect(() => {
    if (currentView === 'all') {
      loadCommercialDescriptions([...lines, ...editedLines])
    }
  }, [currentView, lines, editedLines])

  // Log opportunity field editability
  useEffect(() => {
    if (quotationDetails && smmParameters) {
      const isDraft = quotationDetails.mserp_alg_portalstatus === 200000000
      const allowOpportunityEdit = smmParameters.mserp_alg_allowopportunityfield === 200000001
      const isOpportunityEditable = isDraft && allowOpportunityEdit && isEditMode
      console.log(`QuotationLinesView: Opportunity field is ${isOpportunityEditable ? 'editable' : 'read-only'} (Draft: ${isDraft}, Allow Edit: ${allowOpportunityEdit}, Edit Mode: ${isEditMode})`)
    }
  }, [quotationDetails, smmParameters, isEditMode])

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const loadQuotationDetails = async () => {
    try {
      console.log('Loading quotation details for:', quotationNumber)

      // Use new efficient endpoint to get single quotation by number
      const result = await dataService.getQuotationByNumber(quotationNumber)

      console.log('Quotation API result:', result)

      if (result.success && result.data) {
        const quotation = result.data

        setQuotationDetails(quotation)
        setEditedQuotationDetails({ ...quotation }) // Initialize editable copy
        console.log('Quotation details loaded:', quotation.mserp_salesquotationnumber)
        console.log('Contact person ID from quotation:', quotation.mserp_contactpersonid)

        // Load contact person if contactpersonid is available
        if (quotation.mserp_contactpersonid) {
          console.log('Loading contact person for ID:', quotation.mserp_contactpersonid)
          loadContactPerson(quotation.mserp_contactpersonid)
        } else {
          console.log('No contact person ID found in quotation')
        }
      } else {
        console.log('Quotation not found:', quotationNumber)
        // Set fallback data
        const fallbackDetails = {
          mserp_salesquotationnumber: quotationNumber,
          mserp_alg_salesquotationname: t('quotation:lines.notFound'),
          mserp_salesquotationname: t('quotation:lines.notFound'),
          mserp_requestingcustomeraccountnumber: t('common:labels.na'),
          mserp_alg_portalstatus: 'Draft',
          createdon: null,
          modifiedon: null,
          mserp_dataareaid: t('common:labels.na')
        }
        setQuotationDetails(fallbackDetails)
        setEditedQuotationDetails({ ...fallbackDetails })
      }
    } catch (error) {
      console.error('Failed to load quotation details:', error)
      // Set fallback data on error
      const errorDetails = {
        mserp_salesquotationnumber: quotationNumber,
        mserp_alg_salesquotationname: t('quotation:lines.errorLoading'),
        mserp_salesquotationname: t('quotation:lines.errorLoading'),
        mserp_requestingcustomeraccountnumber: t('quotation:lines.errorLoading'),
        mserp_alg_portalstatus: 'Draft',
        createdon: null,
        modifiedon: null,
        mserp_dataareaid: t('common:labels.na')
      }
      setQuotationDetails(errorDetails)
      setEditedQuotationDetails({ ...errorDetails })
    }
  }

  const loadContactPerson = async (contactPersonId) => {
    try {
      console.log('Loading contact person for ID:', contactPersonId)
      console.log('Note: Backend should filter mserp_smmcontactpersonv2entity by logged user company')
      
      const result = await dataService.getContactPerson(contactPersonId)
      
      if (result.success && result.data) {
        console.log('Contact person loaded successfully:', result.data)
        setContactPerson(result.data)
      } else {
        console.log('Failed to load contact person (may be due to company filtering):', result.error)
        setContactPerson(null)
      }
    } catch (error) {
      console.error('Error loading contact person (check company filtering):', error)
      setContactPerson(null)
    }
  }

  const loadQuotationLines = async ({ showLoading = true } = {}) => {
    if (showLoading) setLoading(true)
    setError(null)
    
    try {
      const result = await dataService.getQuotationLines(quotationNumber)
      
      if (result.success) {
        console.log('=== LOADED QUOTATION LINES ===')
        console.log('Number of lines:', result.data.length)
        if (result.data.length > 0) {
          console.log('First line fields:', Object.keys(result.data[0]))
          console.log('First line data:', result.data[0])
          // Check for various possible entity ID field names
          const possibleIdFields = [
            'mserp_salesquotationlinev2entityid',
            'mserp_alg_salesquotationlinev2entityid', 
            'id',
            '@odata.id',
            'Id',
            'ID'
          ]
          possibleIdFields.forEach(field => {
            if (result.data[0][field]) {
              console.log(`Found ID field "${field}":`, result.data[0][field])
            }
          })
        }
        // Preserve existing defaults, but fill the field when Dataverse returns it empty.
        const processedLines = result.data.map(line => {
          const processedLine = { ...line }

          const salesPrice = Number(processedLine.mserp_salesprice)
          const linePercent = Number(processedLine.mserp_linediscountpercentage) || 0
          const defaultNetSalesPrice = Number(processedLine.mserp_alg_defaultnetsalesprice)

          if (
            salesPrice > 0 &&
            (!Number.isFinite(defaultNetSalesPrice) || defaultNetSalesPrice <= 0)
          ) {
            const calculatedValue = calculateDefaultNetSalesPrice(salesPrice, linePercent)
            if (calculatedValue > 0) {
              processedLine.mserp_alg_defaultnetsalesprice = calculatedValue
              console.log(
                `Filled missing ALG_DefaultNetSalesPrice: ${salesPrice} x (1-${linePercent}/100) = ${calculatedValue} for line:`,
                processedLine.mserp_itemnumber || 'No item number'
              )
            }
          }

          const netSalesPrice = Number(processedLine.mserp_alg_netsalesprice)
          const resolvedDefaultNetSalesPrice = Number(processedLine.mserp_alg_defaultnetsalesprice)
          if (
            (!Number.isFinite(netSalesPrice) || netSalesPrice <= 0) &&
            Number.isFinite(resolvedDefaultNetSalesPrice) &&
            resolvedDefaultNetSalesPrice > 0
          ) {
            processedLine.mserp_alg_netsalesprice = resolvedDefaultNetSalesPrice
          }

          return processedLine
        })

        // Sort lines by Ord field (mserp_alg_reportdisplayorder) in ascending order
        const sortedLines = processedLines.sort((a, b) => {
          const aOrd = parseInt(a.mserp_alg_reportdisplayorder) || 0
          const bOrd = parseInt(b.mserp_alg_reportdisplayorder) || 0
          return aOrd - bOrd
        })

        setLines(sortedLines)

        // Newly created quotation: open directly in edit mode so the user can add lines
        if (startInEditMode && !startInEditModeAppliedRef.current) {
          startInEditModeAppliedRef.current = true
          const prepared = prepareLinesForEditing(sortedLines)
          setEditedLines(prepared.lines)
          setModifiedFields(prepared.modifiedFields)
          setIsEditMode(true)
        } else {
          setEditedLines(sortedLines.map(line => ({ ...line }))) // Create a copy for editing
        }
      } else {
        setError(result.error || t('quotation:lines.failedToFetch'))
      }
    } catch (error) {
      setError(error.message || t('quotation:lines.errorFetching'))
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return ''
    // Format as decimal number without currency symbol
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  const formatQuantity = (quantity) => {
    if (!quantity || isNaN(quantity)) return ''
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(quantity)
  }

  const handleEditToggle = () => {
    // Prevent editing if quotation is not in Draft status
    if (isQuotationReadOnly()) {
      setError(t('quotation:lines.editNotAllowed'))
      return
    }
    
    if (isEditMode) {
      // Save changes or cancel
      setEditedLines(lines.map(line => ({ ...line }))) // Reset to original
      setModifiedFields(new Set()) // Clear modified fields when exiting edit mode
      setQuotationModifiedFields(new Set()) // Clear quotation modified fields
    } else {
      // Entering edit mode - ensure editedLines is synced with current lines
      const prepared = prepareLinesForEditing(lines)
      setEditedLines(prepared.lines)
      setModifiedFields(prepared.modifiedFields)
    }
    setIsEditMode(!isEditMode)
    setSelectedLines(new Set()) // Clear selection when toggling edit mode
  }

  const handleCellEdit = (index, field, value) => {
    console.log(`Editing field: ${field} at index ${index} with value:`, value)
    
    // Ensure we're in edit mode
    if (!isEditMode) {
      console.log('Not in edit mode, enabling edit mode')
      setIsEditMode(true)
    }
    
    const newEditedLines = [...editedLines]
    newEditedLines[index] = { ...newEditedLines[index], [field]: value }
    
    // If Net Sales Price is modified, recalculate Line Discount Percentage
    // Formula: LineDiscount = (1 - ALG_NetSalesPrice/SalesPrice) × 100
    if (field === 'mserp_alg_netsalesprice') {
      const salesPrice = newEditedLines[index].mserp_salesprice || 0
      const netSalesPrice = value || 0
      const defaultNetSalesPrice = newEditedLines[index].mserp_alg_defaultnetsalesprice || 0
      
      if (salesPrice > 0) {
        const discountPercentage = (1 - netSalesPrice / salesPrice) * 100
        newEditedLines[index].mserp_linediscountpercentage = Math.round(discountPercentage * 100) / 100 // Round to 2 decimal places
        
        // Mark discount field as modified too
        const discountFieldKey = `${index}-mserp_linediscountpercentage`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, discountFieldKey])
          return newSet
        })
        
        console.log(`Auto-calculated discount: ${discountPercentage.toFixed(2)}% for line ${index}`)
      } else if (defaultNetSalesPrice > 0) {
        const discountPercentage = (1 - netSalesPrice / defaultNetSalesPrice) * 100
        newEditedLines[index].mserp_linediscountpercentage = Math.round(discountPercentage * 100) / 100

        const discountFieldKey = `${index}-mserp_linediscountpercentage`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, discountFieldKey])
          return newSet
        })

        console.log(`Auto-calculated custom-line discount: ${discountPercentage.toFixed(2)}% for line ${index}`)
      }
      
      // For Out of Stock items, set ALG_VariationPercentage equal to LinePercent
      // For other items, recalculate ALG_VariationPercentage normally
      const productType = newEditedLines[index].mserp_alg_producttype
      if (productType === '200000001' || productType === 200000001) {
        const linePercent = newEditedLines[index].mserp_linediscountpercentage || 0
        newEditedLines[index].mserp_alg_variationpercentage = linePercent
        const variationFieldKey = `${index}-mserp_alg_variationpercentage`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, variationFieldKey])
          return newSet
        })
        console.log(`Set ALG_VariationPercentage to LinePercent value: ${linePercent}% for Out of Stock item (from NetSalesPrice change)`)
      } else {
        // Also recalculate ALG_VariationPercentage when ALG_NetSalesPrice changes
        updateVariationPercentage(newEditedLines[index], index, 'from ALG_NetSalesPrice change')
      }
      
      // Recalculate LineAmount
      const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
      const lineAmount = calculateLineAmount(requestedQty, netSalesPrice)
      console.log(`Auto-calculated line amount: ${requestedQty} × ${netSalesPrice} = ${lineAmount}`)

      newEditedLines[index] = { ...newEditedLines[index], mserp_lineamount: lineAmount }

      // Mark line amount field as modified too
      const lineAmountFieldKey = `${index}-mserp_lineamount`
      setModifiedFields(prev => {
        const newSet = new Set([...prev, lineAmountFieldKey])
        return newSet
      })
    }
    
    // If Sales Price is modified, recalculate Net Sales Price using discount, then Line Amount
    if (field === 'mserp_salesprice') {
      const salesPrice = value || 0
      const discountPercentage = newEditedLines[index].mserp_linediscountpercentage || 0

      if (salesPrice > 0) {
        // Recalculate Net Sales Price = SalesPrice × (1 - Discount%/100)
        const netSalesPrice = salesPrice * (1 - discountPercentage / 100)
        newEditedLines[index].mserp_alg_netsalesprice = Math.round(netSalesPrice * 100) / 100

        // Mark net sales price field as modified too
        const netSalesPriceFieldKey = `${index}-mserp_alg_netsalesprice`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, netSalesPriceFieldKey])
          return newSet
        })

        console.log(`Auto-calculated net sales price after sales price change: ${salesPrice} × (1 - ${discountPercentage}%/100) = ${netSalesPrice.toFixed(2)} for line ${index}`)

        // Recalculate Line Amount = RequestedQty × NetSalesPrice
        const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
        const lineAmount = calculateLineAmount(requestedQty, netSalesPrice)
        newEditedLines[index] = { ...newEditedLines[index], mserp_lineamount: lineAmount }

        // Mark line amount field as modified too
        const lineAmountFieldKey = `${index}-mserp_lineamount`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, lineAmountFieldKey])
          return newSet
        })

        console.log(`Auto-calculated line amount: ${requestedQty} × ${netSalesPrice.toFixed(2)} = ${lineAmount} for line ${index}`)
      }
    }
    
    // If Line Discount Percentage is modified, recalculate Net Sales Price
    // Formula: ALG_NetSalesPrice = SalesPrice × (1 - LinePercent/100)
    if (field === 'mserp_linediscountpercentage') {
      const salesPrice = newEditedLines[index].mserp_salesprice || 0
      const discountPercentage = value || 0
      const defaultNetSalesPrice = newEditedLines[index].mserp_alg_defaultnetsalesprice || 0
      
      if (salesPrice > 0) {
        const netSalesPrice = salesPrice * (1 - discountPercentage / 100)
        newEditedLines[index] = { ...newEditedLines[index], mserp_alg_netsalesprice: Math.round(netSalesPrice * 100) / 100 }
        
        // Mark net sales price field as modified too
        const netSalesPriceFieldKey = `${index}-mserp_alg_netsalesprice`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, netSalesPriceFieldKey])
          return newSet
        })
        
        console.log(`Auto-calculated net sales price: ${netSalesPrice.toFixed(2)} for line ${index} with discount ${discountPercentage}%`)
        
        // For Out of Stock items, set ALG_VariationPercentage equal to LinePercent
        // For other items, recalculate ALG_VariationPercentage normally
        const productType = newEditedLines[index].mserp_alg_producttype
        if (productType === '200000001' || productType === 200000001) {
          newEditedLines[index] = { ...newEditedLines[index], mserp_alg_variationpercentage: discountPercentage }
          const variationFieldKey = `${index}-mserp_alg_variationpercentage`
          setModifiedFields(prev => {
            const newSet = new Set([...prev, variationFieldKey])
            return newSet
          })
          console.log(`Set ALG_VariationPercentage to LinePercent value: ${discountPercentage}% for Out of Stock item`)
        } else {
          updateVariationPercentage(newEditedLines[index], index, 'from LinePercent change')
        }
        
        // Recalculate LineAmount = Requested Qty × ALG_NetSalesPrice
        const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
        const lineAmount = calculateLineAmount(requestedQty, netSalesPrice)
        newEditedLines[index] = { ...newEditedLines[index], mserp_lineamount: lineAmount }
        
        // Mark line amount field as modified too
        const lineAmountFieldKey = `${index}-mserp_lineamount`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, lineAmountFieldKey])
          return newSet
        })

      } else if (defaultNetSalesPrice > 0) {
        const netSalesPrice = defaultNetSalesPrice * (1 - discountPercentage / 100)
        newEditedLines[index] = {
          ...newEditedLines[index],
          mserp_alg_netsalesprice: Math.round(netSalesPrice * 100) / 100
        }

        const netSalesPriceFieldKey = `${index}-mserp_alg_netsalesprice`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, netSalesPriceFieldKey])
          return newSet
        })

        const productType = newEditedLines[index].mserp_alg_producttype
        if (productType === '200000001' || productType === 200000001) {
          newEditedLines[index].mserp_alg_variationpercentage = discountPercentage
          const variationFieldKey = `${index}-mserp_alg_variationpercentage`
          setModifiedFields(prev => {
            const newSet = new Set([...prev, variationFieldKey])
            return newSet
          })
        } else {
          updateVariationPercentage(newEditedLines[index], index, 'from custom-line discount change')
        }

        const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
        const lineAmount = calculateLineAmount(requestedQty, netSalesPrice)
        newEditedLines[index] = { ...newEditedLines[index], mserp_lineamount: lineAmount }

        const lineAmountFieldKey = `${index}-mserp_lineamount`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, lineAmountFieldKey])
          return newSet
        })

        console.log(`Auto-calculated custom-line net sales price: ${defaultNetSalesPrice} × (1 - ${discountPercentage}%/100) = ${netSalesPrice}`)
      }
    }
    
    // If Requested Qty is modified, recalculate LineAmount
    // Formula: LineAmount = Requested Qty × ALG_NetSalesPrice
    if (field === 'mserp_requestedsalesquantity') {
      const requestedQty = value || 0
      const netSalesPrice = newEditedLines[index].mserp_alg_netsalesprice || 0
      
      if (requestedQty > 0 && netSalesPrice > 0) {
        const lineAmount = calculateLineAmount(requestedQty, netSalesPrice)
        newEditedLines[index] = { ...newEditedLines[index], mserp_lineamount: lineAmount }
        
        // Mark line amount field as modified too
        const lineAmountFieldKey = `${index}-mserp_lineamount`
        setModifiedFields(prev => {
          const newSet = new Set([...prev, lineAmountFieldKey])
          return newSet
        })
        
        console.log(`Auto-calculated line amount from qty change: ${requestedQty} × ${netSalesPrice} = ${lineAmount} for line ${index}`)
      }
    }

    // If Line Amount is modified for Existing products with NO sales price, recalculate Net Sales Price
    // Formula: NetSalesPrice = LineAmount / (Qty × (1 - Discount%/100))
    if (field === 'mserp_lineamount') {
      const productType = newEditedLines[index].mserp_alg_producttype
      const salesPrice = newEditedLines[index].mserp_salesprice || 0

      if ((productType === '200000000' || productType === 200000000) && salesPrice === 0) {
        const lineAmount = value || 0
        const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
        const discountPercentage = newEditedLines[index].mserp_linediscountpercentage || 0

        if (requestedQty > 0) {
          // Work backwards: NetSalesPrice = LineAmount / (Qty × (1 - Discount%/100))
          const priceMultiplier = 1 - (discountPercentage / 100)
          const netSalesPrice = lineAmount / (requestedQty * priceMultiplier)
          newEditedLines[index] = { ...newEditedLines[index], mserp_alg_netsalesprice: Math.round(netSalesPrice * 100) / 100 }

          // Mark net sales price field as modified too
          const netSalesPriceFieldKey = `${index}-mserp_alg_netsalesprice`
          setModifiedFields(prev => {
            const newSet = new Set([...prev, netSalesPriceFieldKey])
            return newSet
          })

          console.log(`Existing product (no sales price) line amount change: NetSalesPrice = ${lineAmount} / (${requestedQty} × (1 - ${discountPercentage}%/100)) = ${netSalesPrice}`)
        }
      }
    }

    setEditedLines(newEditedLines)
    
    // Mark this field as modified
    const fieldKey = `${index}-${field}`
    console.log(`Marking field as modified: ${fieldKey}`)
    setModifiedFields(prev => {
      const newSet = new Set([...prev, fieldKey])
      console.log('All modified fields:', Array.from(newSet))
      console.log('Has unsaved changes will be:', newSet.size > 0)
      return newSet
    })
  }

  const loadCommercialDescriptions = async (quotationLines) => {
    const productNumbers = [...new Set(
      quotationLines
        .map(line => line.mserp_productnumber)
        .filter(Boolean)
    )]

    if (productNumbers.length === 0) {
      setCommercialDescriptions({})
      return
    }

    const productNumbersToLoad = productNumbers.filter(productNumber =>
      !Object.prototype.hasOwnProperty.call(commercialDescriptions, productNumber) &&
      !commercialDescriptionRequestsRef.current.has(productNumber)
    )
    if (productNumbersToLoad.length === 0) return

    const descriptions = {}
    await Promise.all(productNumbersToLoad.map((productNumber) => {
      const request = dataService.getProductAttributes(productNumber, 1)
        .then(result => {
          if (result.success) {
            const commercialDescription = result.data[0]
            descriptions[productNumber] = commercialDescription?.mserp_textvalue || ''
          }
        })
        .catch(error => {
          console.error(`Error loading commercial description for ${productNumber}:`, error)
        })
      commercialDescriptionRequestsRef.current.set(productNumber, request)
      return request
    }))
    if (Object.keys(descriptions).length > 0) {
      setCommercialDescriptions(previous => ({ ...previous, ...descriptions }))
    }
  }

  const handleItemSelect = async (index, product) => {
    const itemNumber = product?.mserp_itemnumber || product?.mserp_productnumber
    if (!itemNumber) return

    setEditedLines(prevLines => {
      const newEditedLines = [...prevLines]
      newEditedLines[index] = {
        ...newEditedLines[index],
        mserp_itemnumber: itemNumber,
        mserp_productsearchname: product?.mserp_productsearchname || '',
        mserp_productnumber: product?.mserp_productnumber || itemNumber,
        _orderQuantitySettings: null,
        _orderQuantityLoading: true,
        _orderQuantityError: null
      }
      return newEditedLines
    })

    const requestKey = itemNumber
    let request = orderQuantityRequestsRef.current.get(requestKey)
    if (!request) {
      request = dataService.getProductOrderSettings(itemNumber)
      orderQuantityRequestsRef.current.set(requestKey, request)
    }

    try {
      const result = await request
      setEditedLines(prevLines => {
        const currentLine = prevLines[index]
        if (!currentLine || currentLine.mserp_itemnumber !== itemNumber) return prevLines

        const nextLines = [...prevLines]
        if (!result.success || !result.data?.hasSettings) {
          nextLines[index] = {
            ...currentLine,
            _orderQuantityLoading: false,
            _orderQuantityError: result.error || 'quantitySettingsUnavailable'
          }
          return nextLines
        }

        const settings = result.data
        const defaultQuantity = getDefaultOrderQuantity(settings)
        nextLines[index] = {
          ...currentLine,
          mserp_requestedsalesquantity: defaultQuantity,
          _orderQuantitySettings: settings,
          _orderQuantityLoading: false,
          _orderQuantityError: null,
          mserp_lineamount: calculateLineAmount(defaultQuantity, currentLine.mserp_alg_netsalesprice || 0)
        }
        return nextLines
      })
      setModifiedFields(previous => new Set([
        ...previous,
        `${index}-mserp_requestedsalesquantity`,
        `${index}-mserp_lineamount`
      ]))
    } catch (error) {
      setEditedLines(prevLines => {
        const currentLine = prevLines[index]
        if (!currentLine || currentLine.mserp_itemnumber !== itemNumber) return prevLines
        const nextLines = [...prevLines]
        nextLines[index] = {
          ...currentLine,
          _orderQuantityLoading: false,
          _orderQuantityError: error.message || 'quantitySettingsUnavailable'
        }
        return nextLines
      })
    } finally {
      orderQuantityRequestsRef.current.delete(requestKey)
    }
  }

  useEffect(() => {
    if (!isEditMode) return

    const productNumbers = [...new Set(
      editedLines
        .filter(line => (
          line.mserp_alg_producttype !== '200000001' &&
          line.mserp_alg_producttype !== 200000001 &&
          line.mserp_itemnumber &&
          !line._orderQuantitySettings &&
          !line._orderQuantityLoading &&
          !line._orderQuantityError
        ))
        .map(line => line.mserp_itemnumber)
    )]

    if (productNumbers.length === 0) return

    setEditedLines(previousLines => previousLines.map(line => (
      productNumbers.includes(line.mserp_itemnumber)
        ? { ...line, _orderQuantityLoading: true }
        : line
    )))

    productNumbers.forEach(itemNumber => {
      let request = orderQuantityRequestsRef.current.get(itemNumber)
      if (!request) {
        request = dataService.getProductOrderSettings(itemNumber)
        orderQuantityRequestsRef.current.set(itemNumber, request)
      }

      Promise.resolve(request)
        .then(result => {
          setEditedLines(previousLines => previousLines.map(line => {
            if (line.mserp_itemnumber !== itemNumber) return line
            if (!result.success || !result.data?.hasSettings) {
              return {
                ...line,
                _orderQuantityLoading: false,
                _orderQuantityError: result.error || 'quantitySettingsUnavailable'
              }
            }
            return {
              ...line,
              _orderQuantitySettings: result.data,
              _orderQuantityLoading: false,
              _orderQuantityError: null
            }
          }))
        })
        .catch(error => {
          setEditedLines(previousLines => previousLines.map(line => (
            line.mserp_itemnumber === itemNumber
              ? {
                  ...line,
                  _orderQuantityLoading: false,
                  _orderQuantityError: error.message || 'quantitySettingsUnavailable'
                }
              : line
          )))
        })
        .finally(() => {
          orderQuantityRequestsRef.current.delete(itemNumber)
        })
    })
  }, [isEditMode, editedLines.length])

  const remapLineIndexes = (previousLines, nextLines, fields = modifiedFields) => {
    const newIndexByLine = new Map(
      previousLines.map(line => [line, nextLines.indexOf(line)])
    )

    const reindexedFields = new Set()
    fields.forEach(fieldKey => {
      const [oldIndex, field] = fieldKey.split('-')
      const newIndex = newIndexByLine.get(previousLines[parseInt(oldIndex)])
      if (newIndex !== undefined) {
        reindexedFields.add(`${newIndex}-${field}`)
      }
    })

    setSelectedLines(prevSelected => new Set(
      Array.from(prevSelected)
        .map(oldIndex => newIndexByLine.get(previousLines[oldIndex]))
        .filter(newIndex => newIndex !== undefined)
    ))
    setFailedLineIndexes(prevFailed => new Set(
      Array.from(prevFailed)
        .map(oldIndex => newIndexByLine.get(previousLines[oldIndex]))
        .filter(newIndex => newIndex !== undefined)
    ))

    return reindexedFields
  }

  const handleManualOrderCommit = () => {
    if (!isEditMode || editedLines.length < 2) return

    const orderedLines = editedLines
      .map((line, originalIndex) => ({ line, originalIndex }))
      .sort((a, b) => {
        const aOrder = parseInt(a.line.mserp_alg_reportdisplayorder) || 0
        const bOrder = parseInt(b.line.mserp_alg_reportdisplayorder) || 0
        return aOrder - bOrder || a.originalIndex - b.originalIndex
      })
      .map(({ line }) => line)

    if (orderedLines.every((line, index) => line === editedLines[index])) return

    setEditedLines(orderedLines)
    setModifiedFields(remapLineIndexes(editedLines, orderedLines))
  }

  const handleLineDragStart = (event, index) => {
    if (!isEditMode || isQuotationReadOnly()) {
      event.preventDefault()
      return
    }

    setDraggedLineIndex(index)
    setDragOverLineIndex(index)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(index))
  }

  const handleLineDragOver = (event, index) => {
    if (draggedLineIndex !== null) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      setDragOverLineIndex(index)
    }
  }

  const handleLineDrop = (event, targetIndex) => {
    event.preventDefault()

    if (draggedLineIndex === null || draggedLineIndex === targetIndex) {
      setDraggedLineIndex(null)
      setDragOverLineIndex(null)
      return
    }

    const reorderedLines = [...editedLines]
    const [draggedLine] = reorderedLines.splice(draggedLineIndex, 1)
    reorderedLines.splice(targetIndex, 0, draggedLine)

    // Keep pending field edits attached to their lines after reordering.
    const reindexedModifiedFields = remapLineIndexes(editedLines, reorderedLines)

    const orderedLines = reorderedLines.map((line, index) => ({
      ...line,
      mserp_alg_reportdisplayorder: (index + 1) * 10
    }))

    orderedLines.forEach((line, index) => {
      reindexedModifiedFields.add(`${index}-mserp_alg_reportdisplayorder`)
    })

    setEditedLines(orderedLines)
    setModifiedFields(reindexedModifiedFields)
    setDraggedLineIndex(null)
    setDragOverLineIndex(null)
  }

  const handleLineDragEnd = () => {
    setDraggedLineIndex(null)
    setDragOverLineIndex(null)
  }

  const handleQuotationFieldEdit = (field, value) => {
    // Handle mutual exclusivity between customer account and prospect
    // onChange is now only called when selecting from dropdown (not during typing)
    if (field === 'mserp_requestingcustomeraccountnumber' && value) {
      // If selecting a customer and prospect exists, warn and clear prospect
      if (editedQuotationDetails.mserp_prospectid) {
        setFieldConflictData({
          field,
          value,
          message: t('quotation:lines.fieldConflictModal.customerClearsProspect'),
          clearField: 'mserp_prospectid'
        })
        setShowFieldConflictModal(true)
        return
      }
    } else if (field === 'mserp_prospectid' && value) {
      // If selecting a prospect and customer account exists, warn and clear customer
      if (editedQuotationDetails.mserp_requestingcustomeraccountnumber) {
        setFieldConflictData({
          field,
          value,
          message: t('quotation:lines.fieldConflictModal.prospectClearsCustomer'),
          clearField: 'mserp_requestingcustomeraccountnumber'
        })
        setShowFieldConflictModal(true)
        return
      }
    }

    // No conflict, proceed with update
    const newEditedQuotationDetails = { ...editedQuotationDetails }
    newEditedQuotationDetails[field] = value
    setEditedQuotationDetails(newEditedQuotationDetails)

    // Mark this quotation field as modified
    setQuotationModifiedFields(prev => new Set([...prev, field]))
  }

  const handleConfirmFieldConflict = () => {
    const { field, value, clearField } = fieldConflictData
    const newEditedQuotationDetails = { ...editedQuotationDetails }

    // Clear the conflicting field
    newEditedQuotationDetails[clearField] = ''
    setQuotationModifiedFields(prev => new Set([...prev, clearField]))

    // Set the new field value
    newEditedQuotationDetails[field] = value
    setEditedQuotationDetails(newEditedQuotationDetails)

    // Mark the new field as modified
    setQuotationModifiedFields(prev => new Set([...prev, field]))

    // Close modal
    setShowFieldConflictModal(false)
    setFieldConflictData({ field: '', value: '', message: '' })
  }

  const handleCancelFieldConflict = () => {
    setShowFieldConflictModal(false)
    setFieldConflictData({ field: '', value: '', message: '' })
  }

  const handleSendToFnO = () => {
    if (!quotationDetails) return
    if (isSendingToErp.current) return

    setShowSendToErpConfirm(true)
  }

  const confirmSendToFnO = async () => {
    if (!quotationDetails) return
    if (isSendingToErp.current) return

    setShowSendToErpConfirm(false)

    isSendingToErp.current = true
    setLoading(true)
    setError(null)

    try {
      console.log('Sending quotation to F&O:', quotationDetails.mserp_salesquotationnumber)

      // Update the portal status to SentToERP (numeric value 200000001)
      const updateData = {
        mserp_alg_portalstatus: 200000001
      }

      const result = await dataService.updateQuotationHeader(
        quotationDetails.mserp_salesquotationnumber,
        updateData
      )

      if (result.success) {
        // Update local state to reflect the change
        const updatedDetails = { ...quotationDetails, mserp_alg_portalstatus: 200000001 }
        setQuotationDetails(updatedDetails)
        setEditedQuotationDetails(updatedDetails)

        console.log('Successfully sent quotation to F&O')
      } else {
        setError(result.error || t('quotation:lines.failedToSend'))
      }
    } catch (err) {
      console.error('Error sending quotation to F&O:', err)
      setError(t('quotation:lines.failedToSendRetry'))
    } finally {
      isSendingToErp.current = false
      setLoading(false)
    }
  }

  const handleChangeToDraft = async () => {
    if (!quotationDetails) return

    setLoading(true)
    setError(null)

    try {
      console.log('Changing quotation to Draft:', quotationDetails.mserp_salesquotationnumber)

      // Update the portal status to Draft (numeric value 200000000)
      const updateData = {
        mserp_alg_portalstatus: 200000000
      }

      const result = await dataService.updateQuotationHeader(
        quotationDetails.mserp_salesquotationnumber,
        updateData
      )

      if (result.success) {
        // Update local state to reflect the change (header only, NOT reloading lines)
        const updatedDetails = { ...quotationDetails, mserp_alg_portalstatus: 200000000 }
        setQuotationDetails(updatedDetails)
        setEditedQuotationDetails(updatedDetails)

        console.log('Successfully changed quotation to Draft - header updated only, lines unchanged')
      } else {
        setError(result.error || t('quotation:lines.failedToChangeDraft'))
      }
    } catch (err) {
      console.error('Error changing quotation to Draft:', err)
      setError(t('quotation:lines.failedToChangeDraftRetry'))
    } finally {
      setLoading(false)
    }
  }

  const handleRefreshQuotationHeader = async () => {
    if (!quotationDetails) return

    console.log('Refreshing quotation header from server:', quotationDetails.mserp_salesquotationnumber)

    try {
      // Reload quotation details from server
      await loadQuotationDetails()
      console.log('Quotation header refreshed successfully')
    } catch (err) {
      console.error('Error refreshing quotation header:', err)
      setError(t('quotation:lines.failedToRefreshHeader'))
    }
  }

  const handleAcceptQuotation = async () => {
    if (!quotationDetails) return
    
    setLoading(true)
    setError(null)
    
    try {
      console.log('Accepting quotation:', quotationDetails.mserp_salesquotationnumber)
      
      // Update the portal status to Accepted (numeric value 200000003)
      const updateData = {
        mserp_alg_portalstatus: 200000003
      }
      
      const result = await dataService.updateQuotationHeader(
        quotationDetails.mserp_salesquotationnumber, 
        updateData
      )
      
      if (result.success) {
        // Update local state to reflect the change
        const updatedDetails = { ...quotationDetails, mserp_alg_portalstatus: 200000003 }
        setQuotationDetails(updatedDetails)
        setEditedQuotationDetails(updatedDetails)
        
        console.log('Successfully accepted quotation')
      } else {
        setError(result.error || t('quotation:lines.failedToAccept'))
      }
    } catch (err) {
      console.error('Error accepting quotation:', err)
      setError(t('quotation:lines.failedToAcceptRetry'))
    } finally {
      setLoading(false)
    }
  }

  const handleRejectQuotation = async () => {
    if (!quotationDetails) return
    
    setLoading(true)
    setError(null)
    
    try {
      console.log('Rejecting quotation:', quotationDetails.mserp_salesquotationnumber)
      
      // Update the portal status to Rejected (numeric value 200000004)
      const updateData = {
        mserp_alg_portalstatus: 200000004
      }
      
      const result = await dataService.updateQuotationHeader(
        quotationDetails.mserp_salesquotationnumber, 
        updateData
      )
      
      if (result.success) {
        // Update local state to reflect the change
        const updatedDetails = { ...quotationDetails, mserp_alg_portalstatus: 200000004 }
        setQuotationDetails(updatedDetails)
        setEditedQuotationDetails(updatedDetails)
        
        console.log('Successfully rejected quotation')
      } else {
        setError(result.error || t('quotation:lines.failedToReject'))
      }
    } catch (err) {
      console.error('Error rejecting quotation:', err)
      setError(t('quotation:lines.failedToRejectRetry'))
    } finally {
      setLoading(false)
    }
  }

  const handleCancelQuotation = async () => {
    if (!quotationDetails) return
    
    setLoading(true)
    setError(null)
    
    try {
      console.log('Cancelling quotation:', quotationDetails.mserp_salesquotationnumber)
      
      // Update the portal status to Cancelled (numeric value 200000005)
      const updateData = {
        mserp_alg_portalstatus: 200000005
      }
      
      const result = await dataService.updateQuotationHeader(
        quotationDetails.mserp_salesquotationnumber, 
        updateData
      )
      
      if (result.success) {
        // Update local state to reflect the change
        const updatedDetails = { ...quotationDetails, mserp_alg_portalstatus: 200000005 }
        setQuotationDetails(updatedDetails)
        setEditedQuotationDetails(updatedDetails)
        
        console.log('Successfully cancelled quotation')
      } else {
        setError(result.error || t('quotation:lines.failedToCancel'))
      }
    } catch (err) {
      console.error('Error cancelling quotation:', err)
      setError(t('quotation:lines.failedToCancelRetry'))
    } finally {
      setLoading(false)
    }
  }

  const handleSaveChanges = async () => {
    console.log('=== handleSaveChanges CALLED ===')
    console.log('modifiedFields:', Array.from(modifiedFields))
    console.log('modifiedFields.size:', modifiedFields.size)
    console.log('deletedLines.length:', deletedLines.length)
    console.log('editedLines with _isNew:', editedLines.filter(line => line._isNew).length)
    console.log('quotationModifiedFields:', Array.from(quotationModifiedFields))
    
    // Only show loading for lines if we're actually going to modify lines
    const hasLineChanges = modifiedFields.size > 0 || deletedLines.length > 0 || editedLines.some(line => line._isNew)
    
    console.log('hasLineChanges:', hasLineChanges)
    
    if (hasLineChanges) {
      setLoading(true)
    }
    setError(null)
    setWarning(null)
    setFailedLineIndexes(new Set())

    const invalidQuantityLines = getInvalidOrderQuantityLines(editedLines)

    if (invalidQuantityLines.length > 0) {
      const firstInvalid = invalidQuantityLines[0]
      setFailedLineIndexes(new Set(invalidQuantityLines.map(({ index }) => index)))
      if (firstInvalid.line._orderQuantityError) {
        setError(t('quotation:lines.quantitySettingsUnavailable'))
      } else {
        const errorKey = getOrderQuantityErrorKey(firstInvalid.validation)
        setError(t(errorKey || 'quotation:lines.quantityInvalid', firstInvalid.validation))
      }
      if (hasLineChanges) setLoading(false)
      return
    }

    // Keep track of updated lines throughout the save process
    let updatedEditedLines = [...editedLines]
    
    try {
      console.log('Saving changes...')
      console.log('Current edited lines:', editedLines.length, 'lines')
      console.log('Original lines count:', lines.length)
      console.log('Deleted lines:', deletedLines.length, 'lines')
      console.log('Modified fields:', Array.from(modifiedFields))
      
      // Handle deletions first
      if (deletedLines.length > 0) {
        console.log('🗑️ Processing deletions:', deletedLines)
        console.log('🗑️ Total deleted lines state:', deletedLines.length)
        
        // Filter out local deletion markers and only process real backend deletions
        const backendDeletions = deletedLines.filter(line => line.mserp_salesquotationlinev2entityid || line.mserp_alg_salesquotationlinev2entityid)
        console.log('🗑️ Backend deletions to process:', backendDeletions.length, backendDeletions)
        
        for (const lineToDelete of backendDeletions) {
          try {
            // Use the correct entity ID field (prefer mserp_alg_ variant if available)
            const entityId = lineToDelete.mserp_alg_salesquotationlinev2entityid || lineToDelete.mserp_salesquotationlinev2entityid
            console.log('Deleting line with ID:', entityId, 'from line:', lineToDelete)
            const deleteResult = await dataService.deleteQuotationLine(entityId)
            if (!deleteResult.success) {
              console.error('Failed to delete line:', lineToDelete, deleteResult.error)
              setError(t('quotation:lines.failedToDeleteLine', { error: deleteResult.error }))
              if (hasLineChanges) setLoading(false)
              return
            }
            console.log('Successfully deleted line:', entityId)
          } catch (deleteError) {
            console.error('Error deleting line:', deleteError)
            setError(t('quotation:lines.errorDeletingLine', { error: deleteError.message }))
            if (hasLineChanges) setLoading(false)
            return
          }
        }
        
        console.log('🗑️ All deletions completed successfully')
        console.log('🗑️ Successfully deleted', backendDeletions.length, 'lines from backend')
        // Note: Lines are already removed from editedLines during visual deletion
        // No need to filter them again here
      }
      
      // Handle creation of new lines first and update them with real IDs
      const newLines = editedLines.filter(line => line._isNew)

      if (newLines.length > 0) {
        console.log('Creating new lines:', newLines)
        const lineErrors = []
        const failedIndexes = new Set()

        for (let i = 0; i < newLines.length; i++) {
          const newLine = newLines[i]
           const lineLabel = newLine._importRow
             ? t('quotation:lines.import.row', { row: newLine._importRow })
             : (newLine.mserp_itemnumber ? `${newLine.mserp_itemnumber} - Line ${i + 1}` : `Line ${i + 1}`)
          try {
            console.log('Creating new line:', newLine)

            // Filter out 0-value fields that should not be sent to the backend
            const filteredNewLine = filterZeroValueFields(newLine, 'new line creation')

            console.log('Original line:', newLine)
            console.log('Filtered line for API:', filteredNewLine)

            // Use the actual quotation number from quotation details, not the prop (which might be the name)
            const actualQuotationNumber = quotationDetails?.mserp_salesquotationnumber || quotationNumber
            console.log(`Using quotation number: ${actualQuotationNumber} (from details) instead of: ${quotationNumber} (prop)`)

            const createResult = await dataService.addQuotationLine(actualQuotationNumber, filteredNewLine)
            if (!createResult.success) {
              console.error('Failed to create line:', newLine, createResult.error)

              // Check if it's the "quotation doesn't exist" error - this might be a false positive
              if (createResult.error && createResult.error.includes('QuotationId') && createResult.error.includes('does not exist')) {
                console.log('Got quotation not found error - checking if line was actually created...')
                // Don't return error immediately - continue and reload lines at the end
                setWarning(t('quotation:lines.lineCreatedDespiteError'))
              } else {
                const editedIndex = editedLines.findIndex(l => l.id === newLine.id)
                if (editedIndex !== -1) failedIndexes.add(editedIndex)
                lineErrors.push({ lineLabel, error: createResult.error })
                continue
              }
            } else {
              console.log('Successfully created line:', createResult)
            }

            // Update the line in our local state with the real ID and remove _isNew flag
            const lineIndex = updatedEditedLines.findIndex(line => line.id === newLine.id)
            if (lineIndex !== -1) {
              const updatedLine = {
                ...updatedEditedLines[lineIndex],
                ...(createResult.data || {}),
                _isNew: false // Remove the new flag
              }

              // Fill a missing default once; preserve values returned by Dataverse.
              const salesPrice = Number(updatedLine.mserp_salesprice)
              const linePercent = Number(updatedLine.mserp_linediscountpercentage) || 0
              const defaultNetSalesPrice = Number(updatedLine.mserp_alg_defaultnetsalesprice)

              if (
                salesPrice > 0 &&
                (!Number.isFinite(defaultNetSalesPrice) || defaultNetSalesPrice <= 0)
              ) {
                const calculatedValue = calculateDefaultNetSalesPrice(salesPrice, linePercent)
                if (calculatedValue > 0) {
                  updatedLine.mserp_alg_defaultnetsalesprice = calculatedValue
                  console.log(`Filled missing ALG_DefaultNetSalesPrice for newly created line: ${salesPrice} x (1-${linePercent}/100) = ${calculatedValue}`)
                }
              }

              updatedEditedLines[lineIndex] = updatedLine
              console.log('Updated local line with backend data:', updatedEditedLines[lineIndex])
            }
          } catch (createError) {
            console.error('Error creating line:', createError)
            const editedIndex = editedLines.findIndex(l => l.id === newLine.id)
            if (editedIndex !== -1) failedIndexes.add(editedIndex)
            lineErrors.push({ lineLabel, error: createError.message })
            continue
          }
        }

        // Always persist successfully created lines (_isNew: false) and reload from server
        setEditedLines(updatedEditedLines)

        if (lineErrors.length > 0) {
          setFailedLineIndexes(failedIndexes)
          const details = lineErrors.map(e => `• ${e.lineLabel}: ${e.error}`).join('\n')
          setError(t('quotation:lines.someLinesFailed', { count: lineErrors.length, details }))
          if (hasLineChanges) setLoading(false)
          return
        }

        await loadQuotationLines({ showLoading: false })
        setFailedLineIndexes(new Set())
        console.log('All new lines created successfully')
      }
      
      // Handle quotation header updates
      if (quotationModifiedFields.size > 0) {
        console.log('Updating quotation header fields:', Array.from(quotationModifiedFields))
        
        // Build header data with only modified fields
        const headerData = {}
        quotationModifiedFields.forEach(field => {
          if (editedQuotationDetails[field] !== undefined) {
            headerData[field] = editedQuotationDetails[field]
          }
        })
        
        try {
          const headerUpdateResult = await dataService.updateQuotationHeader(quotationNumber, headerData)
          if (!headerUpdateResult.success) {
            console.error('Failed to update quotation header:', headerUpdateResult.error)
            setError(t('quotation:lines.failedToUpdateHeader', { error: headerUpdateResult.error }))
            if (hasLineChanges) setLoading(false)
            return
          }
          console.log('Quotation header updated successfully')
          
          // Update the main quotation details with the new values (selective refresh)
          const updatedQuotationDetails = { ...quotationDetails, ...headerData }
          setQuotationDetails(updatedQuotationDetails)
          setEditedQuotationDetails({ ...updatedQuotationDetails })
          
        } catch (headerError) {
          console.error('Error updating quotation header:', headerError)
          setError(t('quotation:lines.errorUpdatingHeader', { error: headerError.message }))
          if (hasLineChanges) setLoading(false)
          return
        }
      }
      
      // Handle updates for existing lines only
      const modifiedLineIndexes = new Set()
      modifiedFields.forEach(fieldKey => {
        const [index] = fieldKey.split('-')
        modifiedLineIndexes.add(parseInt(index))
      })
      
      console.log('Modified line indexes:', Array.from(modifiedLineIndexes))
      console.log('Total lines in updatedEditedLines:', updatedEditedLines.length)
      
      const linesToUpdate = updatedEditedLines.filter((line, index) => {
        const isModified = modifiedLineIndexes.has(index)
        // Check multiple possible entity ID field names
        const entityId = line.mserp_salesquotationlinev2entityid || 
                        line.mserp_alg_salesquotationlinev2entityid || 
                        line.id
        const hasEntityId = !!entityId
        const isNotNew = !line._isNew
        
        console.log(`Line ${index}: modified=${isModified}, hasEntityId=${hasEntityId}, isNotNew=${isNotNew}, entityId=${entityId}`)
        console.log(`  Checking IDs - mserp_salesquotationlinev2entityid: ${line.mserp_salesquotationlinev2entityid}, mserp_alg_salesquotationlinev2entityid: ${line.mserp_alg_salesquotationlinev2entityid}, id: ${line.id}`)
        
        // Make sure the line has the entity ID for the backend
        if (hasEntityId && !line.mserp_salesquotationlinev2entityid) {
          line.mserp_salesquotationlinev2entityid = entityId
        }
        
        return isModified && hasEntityId && isNotNew
      })
      
      // Update existing lines if there are modifications
      if (linesToUpdate.length > 0) {
        console.log('Updating modified lines:', linesToUpdate.length, 'lines')
        
        // Filter out 0-value fields from lines being updated
        const filteredLinesToUpdate = linesToUpdate.map(line => filterZeroValueFields(line, 'line update'))
        
        console.log('Original lines to update:', linesToUpdate)
        console.log('Filtered lines for update API:', filteredLinesToUpdate)
        
        const updateResult = await dataService.updateQuotationLines(quotationNumber, filteredLinesToUpdate)
        
        if (!updateResult.success) {
          console.error('Update failed:', updateResult.error)
          setError(updateResult.error || t('quotation:lines.failedToUpdateLines'))
          if (hasLineChanges) setLoading(false)
          return
        }
        
        // Check for warnings
        if (updateResult.warning) {
          setWarning(updateResult.warning)
        }
        
        // Check if any lines had individual update errors
        const linesWithErrors = updateResult.data?.filter(line => line.updateError) || []
        if (linesWithErrors.length > 0) {
          console.warn('Some lines had update errors:', linesWithErrors)
          setWarning(t('quotation:lines.linesCouldNotUpdate', { count: linesWithErrors.length }))
        }
        
        console.log('Updates completed successfully - using local data (no reload needed)')
        // Note: updatedEditedLines already contains the modified data, no need to reload
      }
      
      if (linesToUpdate.length === 0 && deletedLines.length === 0 && newLines.length === 0 && quotationModifiedFields.size === 0) {
        console.log('No changes to save')
        if (hasLineChanges) setLoading(false)
        return
      }
      
      // Only update lines state if there were actual line changes (not just header changes)
      const hasActualLineChanges = linesToUpdate.length > 0 || deletedLines.length > 0 || newLines.length > 0
      
      if (hasActualLineChanges) {
        // Update the main lines state with our changes (no need to reload from backend)
        // Filter out any remaining lines marked as _isNew (these would be lines that failed to create)
        const finalLines = updatedEditedLines.filter(line => !line._isNew)
        console.log('Setting final lines (no reload):', finalLines.length, 'lines')
        setLines(finalLines)
        setEditedLines(finalLines.map(line => ({ ...line }))) // Sync editedLines with final state
      } else {
        console.log('Only header changes - no line state update needed')
      }
      
      // Clear all edit state
      setModifiedFields(new Set())
      setQuotationModifiedFields(new Set())
      setFailedLineIndexes(new Set())
      setSelectedLines(new Set())
      setDeletedLines([])
      setIsEditMode(false)
      setWarning(null) // Clear any deletion warnings
      
      console.log('All changes saved successfully')

      // Reload quotation lines from backend to ensure UI matches backend state
      if (hasLineChanges) {
        console.log('Reloading quotation lines from backend to sync state...')
        await loadQuotationLines({ showLoading: false })
      }
        
    } catch (err) {
      console.error('Error saving changes:', err)
      setError(err.message || t('quotation:lines.failedToSaveChanges'))
    } finally {
      if (hasLineChanges) setLoading(false)
    }
  }

  const handleCancelChanges = () => {
    setEditedLines(lines.map(line => ({ ...line })))
    setEditedQuotationDetails(quotationDetails ? { ...quotationDetails } : null)
    setModifiedFields(new Set()) // Clear modified fields when canceling
    setQuotationModifiedFields(new Set()) // Clear quotation modified fields
    setSelectedLines(new Set()) // Clear selection when canceling
    setDeletedLines([]) // Clear deleted lines when canceling
    setIsEditMode(false)
  }

  const calculateNextDisplayOrder = () => {
    // Get all current lines (both existing and newly added)
    const allLines = isEditMode ? editedLines : lines

    if (allLines.length === 0) {
      // First line gets display order 10
      console.log('First line created, setting display order to 10')
      return 10
    }

    // Find the maximum display order value
    const maxDisplayOrder = Math.max(
      ...allLines.map(line => parseInt(line.mserp_alg_reportdisplayorder) || 0)
    )

    const nextDisplayOrder = maxDisplayOrder + 10
    console.log(`Calculated next display order: ${nextDisplayOrder} (max was: ${maxDisplayOrder})`)

    // Return max + 10
    return nextDisplayOrder
  }

  const handleAddNewLine = ({ focusItemInput = false } = {}) => {
    console.log('Adding new line...')

    // Create a new empty line with temporary ID
    // Use the actual quotation number from quotation details, not the prop (which might be the name)
    const actualQuotationNumber = quotationDetails?.mserp_salesquotationnumber || quotationNumber
    const newLineId = `temp_${Date.now()}`

    const newLine = {
      id: newLineId, // Temporary ID for new lines
      mserp_salesquotationnumber: actualQuotationNumber,
      mserp_itemnumber: '',
      mserp_requestedsalesquantity: 1,
      mserp_salespricequantity: 1,
      // Don't set these fields to 0 - let the backend use defaults
      // mserp_salesprice: 0,
      // mserp_lineamount: 0,
      // mserp_linediscountpercentage: 0,
      mserp_alg_reportdisplayorder: calculateNextDisplayOrder(),
      mserp_alg_grouping: '',
      mserp_alg_producttype: '200000000',
      mserp_alg_outofcatalogproduct: '',
      mserp_alg_itemreferenceeasyvent: '',
      mserp_alg_linedisc: '',
      mserp_salesunitsymbol: '',
      mserp_alg_variationpercentage: 0,
      mserp_alg_netsalesprice: 0,
      mserp_alg_defaultnetsalesprice: 0,
      // Both ALG_DefaultNetSalesPrice and ALG_NetSalesPrice will be calculated after line creation
      _isNew: true // Flag to indicate this is a new line
    }
    
    // Add to edited lines
    const newEditedLines = [...editedLines, newLine]
    setEditedLines(newEditedLines)
    if (focusItemInput) {
      setFocusItemInputLineId(newLineId)
    }
    
    // Enable edit mode if not already enabled
    if (!isEditMode) {
      setIsEditMode(true)
    }
    
    console.log('New line added:', newLine)
  }

  const handleLineKeyDown = (event) => {
    if (event.key !== 'Enter' || event.isComposing || !event.target?.matches?.('input, select, textarea')) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (event.target.name === 'line-display-order') {
      handleManualOrderCommit()
    } else {
      handleAddNewLine({ focusItemInput: true })
    }
  }

  /**
   * Duplicates selected lines locally. The duplicated lines are appended to editedLines
   * and marked as _isNew so they appear in red and will be created in the backend on Save.
   */
  const handleDuplicateSelectedLines = () => {
    if (selectedLines.size === 0) return

    console.log('Duplicating selected lines:', Array.from(selectedLines))

    const now = Date.now()
    let counter = 0

    // Sort indexes to duplicate in the same order as selection
    const selectedIndexes = Array.from(selectedLines).sort((a, b) => a - b)

    // Start with the current max display order for calculating new values
    let currentMaxDisplayOrder = Math.max(
      ...editedLines.map(line => parseInt(line.mserp_alg_reportdisplayorder) || 0)
    )

    const duplicates = selectedIndexes.map(index => {
      const original = editedLines[index]
      if (!original) return null

      // Copy relevant fields from the original line, avoid copying backend IDs
      const {
        mserp_salesquotationlinev2entityid,
        mserp_alg_salesquotationlinev2entityid,
        id,
        _isNew,
        ...rest
      } = original

      // Calculate next display order value for this duplicate
      currentMaxDisplayOrder += 10

      const newLine = {
        ...rest,
        id: `temp_${now}_${counter++}`,
        mserp_salesquotationnumber: quotationDetails?.mserp_salesquotationnumber || quotationNumber,
        mserp_alg_reportdisplayorder: currentMaxDisplayOrder,
        _isNew: true
      }

      return newLine
    }).filter(Boolean)

    if (duplicates.length === 0) return

    // Append duplicates to editedLines
    const newEdited = [...editedLines, ...duplicates]
    setEditedLines(newEdited)

    // Enter edit mode if not already
    if (!isEditMode) setIsEditMode(true)

    // Keep selection (optional) - clear selection to avoid accidental duplicate again
    setSelectedLines(new Set())

    // Mark as modified so Save button appears
    const newModifiedKeys = duplicates.map((_, i) => `${editedLines.length + i}-mserp_itemnumber`)
    setModifiedFields(prev => {
      const newSet = new Set(prev)
      newModifiedKeys.forEach(k => newSet.add(k))
      return newSet
    })

    console.log('Duplicated lines added locally:', duplicates)
  }

  const handleDeleteSelectedLines = () => {
    if (selectedLines.size === 0) return
    
    console.log('Marking selected lines for deletion:', Array.from(selectedLines))
    
    // Sort indexes in descending order to delete from the end first
    const sortedIndexes = Array.from(selectedLines).sort((a, b) => b - a)
    
    // Track which lines are being deleted (store the actual line data for backend deletion)
    const linesToDelete = []
    let hasAnyDeletion = false
    
    sortedIndexes.forEach(index => {
      const lineToDelete = editedLines[index]
      hasAnyDeletion = true // Mark that we have deletions regardless of ID
      
      if (lineToDelete && (lineToDelete.mserp_salesquotationlinev2entityid || lineToDelete.mserp_alg_salesquotationlinev2entityid)) {
        // Only track existing lines that have IDs for backend deletion
        linesToDelete.push(lineToDelete)
      }
    })
    
    let newEditedLines = [...editedLines]
    let newModifiedFields = new Set()
    let newFailedLineIndexes = new Set()
    
    // Remove lines from display
    sortedIndexes.forEach(index => {
      newEditedLines = newEditedLines.filter((_, i) => i !== index)
    })
    
    // Rebuild modified fields with adjusted indexes
    modifiedFields.forEach(fieldKey => {
      const [lineIndex, field] = fieldKey.split('-')
      const originalIndex = parseInt(lineIndex)
      
      if (!selectedLines.has(originalIndex)) {
        // Calculate how many lines before this one were deleted
        const deletedBefore = sortedIndexes.filter(delIndex => delIndex < originalIndex).length
        const newIndex = originalIndex - deletedBefore
        newModifiedFields.add(`${newIndex}-${field}`)
      }
    })
    
    // Rebuild failed line indexes with adjusted indexes
    failedLineIndexes.forEach(lineIndex => {
      if (!selectedLines.has(lineIndex)) {
        // Calculate how many lines before this one were deleted
        const deletedBefore = sortedIndexes.filter(delIndex => delIndex < lineIndex).length
        const newIndex = lineIndex - deletedBefore
        newFailedLineIndexes.add(newIndex)
      }
    })
    
    setEditedLines(newEditedLines)
    setModifiedFields(newModifiedFields)
    setFailedLineIndexes(newFailedLineIndexes)
    setSelectedLines(new Set()) // Clear selection
    
    // Track deleted lines - include both backend deletions and local changes
    if (hasAnyDeletion) {
      if (linesToDelete.length > 0) {
        // Add backend lines to delete
        setDeletedLines([...deletedLines, ...linesToDelete])
      } else {
        // Even if no backend lines, mark that we have unsaved changes
        // Add a special marker to track local deletions
        setDeletedLines([...deletedLines, { _localDeletion: true }])
      }
    }
    
    // If not in edit mode, enable it
    if (!isEditMode) {
      setIsEditMode(true)
    }
    
    console.log('Selected lines marked for deletion, remaining lines:', newEditedLines.length)
    console.log('Total lines to be deleted on save:', deletedLines.length + linesToDelete.length)
    
    // Show user feedback that they need to save
    if (linesToDelete.length > 0) {
      console.log(`🗑️ ${linesToDelete.length} line(s) marked for deletion. Click "Save Changes" to permanently delete them from the database.`)
      
      // Set a warning to remind user to save
      setWarning(t('quotation:lines.deletionWarning', { count: linesToDelete.length }))
    }
  }

  const handleSelectLine = (index, isSelected) => {
    const newSelectedLines = new Set(selectedLines)
    if (isSelected) {
      newSelectedLines.add(index)
    } else {
      newSelectedLines.delete(index)
    }
    setSelectedLines(newSelectedLines)
  }

  const handleSelectAllLines = (isSelected) => {
    if (isSelected) {
      const allIndexes = new Set((isEditMode ? editedLines : lines).map((_, index) => index))
      setSelectedLines(allIndexes)
    } else {
      setSelectedLines(new Set())
    }
  }

  const handleGroupSelectedLines = () => {
    if (selectedLines.size === 0) return
    
    console.log('Grouping selected lines:', Array.from(selectedLines))
    
    // Get the first selected line to use its item number as default group name
    const selectedIndexes = Array.from(selectedLines).sort((a, b) => a - b)
    const firstSelectedLine = editedLines[selectedIndexes[0]]
    const defaultGroupName = firstSelectedLine?.mserp_itemnumber || 'Grupo'
    
    setGroupName(defaultGroupName)
    setShowGroupModal(true)
  }

  const handleConfirmGrouping = () => {
    if (selectedLines.size === 0 || !groupName.trim()) return
    
    console.log('Confirming grouping with name:', groupName)
    
    // Update the grouping field for all selected lines
    const selectedIndexes = Array.from(selectedLines)
    
    setEditedLines(prevLines => {
      const newLines = [...prevLines]
      
      selectedIndexes.forEach(index => {
        if (newLines[index]) {
          newLines[index] = {
            ...newLines[index],
            mserp_alg_grouping: groupName.trim()
          }
        }
      })
      
      return newLines
    })
    
    // Mark all modified fields
    setModifiedFields(prev => {
      const newModifiedFields = new Set(prev)
      selectedIndexes.forEach(index => {
        const fieldKey = `${index}-mserp_alg_grouping`
        newModifiedFields.add(fieldKey)
      })
      return newModifiedFields
    })
    
    // Close modal and clear selection
    setShowGroupModal(false)
    setGroupName('')
    setSelectedLines(new Set())
  }

  const handleCancelGrouping = () => {
    setShowGroupModal(false)
    setGroupName('')
  }

  const handleUngroupSelectedLines = () => {
    if (selectedLines.size === 0) return
    
    console.log('Showing ungroup confirmation for selected lines:', Array.from(selectedLines))
    setShowUngroupConfirm(true)
  }

  const handleConfirmUngroup = () => {
    if (selectedLines.size === 0) return
    
    console.log('Confirming ungroup for selected lines:', Array.from(selectedLines))
    
    // Update the grouping field for all selected lines to empty
    const selectedIndexes = Array.from(selectedLines)
    
    setEditedLines(prevLines => {
      const newLines = [...prevLines]
      
      selectedIndexes.forEach(index => {
        if (newLines[index]) {
          newLines[index] = {
            ...newLines[index],
            mserp_alg_grouping: ''
          }
        }
      })
      
      return newLines
    })
    
    // Mark all modified fields
    setModifiedFields(prev => {
      const newModifiedFields = new Set(prev)
      selectedIndexes.forEach(index => {
        const fieldKey = `${index}-mserp_alg_grouping`
        newModifiedFields.add(fieldKey)
      })
      return newModifiedFields
    })
    
    // Close modal and clear selection
    setShowUngroupConfirm(false)
    setSelectedLines(new Set())
  }

  const handleCancelUngroup = () => {
    setShowUngroupConfirm(false)
  }

  const handleBulkLinePercentEdit = () => {
    // Allow opening the modal even without selection (can apply by Line Disc)
    console.log('Opening bulk LinePercent edit')
    if (selectedLines.size > 0) {
      console.log('Selected lines:', Array.from(selectedLines))
    }
    setBulkLinePercent('')
    setApplyByLineDisc(selectedLines.size === 0) // Default to Line Disc mode if no selection
    setSelectedLineDisc('')
    setShowLinePercentModal(true)
  }

  const handleConfirmBulkLinePercent = () => {
    if (bulkLinePercent === '') return

    const percentValue = parseFloat(bulkLinePercent)
    if (isNaN(percentValue)) return

    // Determine which lines to update
    let linesToUpdate = []

    if (applyByLineDisc) {
      // Apply to all lines with the selected Line Disc value (or empty)
      if (!selectedLineDisc) return

      if (selectedLineDisc === '__EMPTY__') {
        // Special case: apply to lines without Line Disc
        console.log('Applying LinePercent:', percentValue, 'to all lines without Line Disc')
        editedLines.forEach((line, index) => {
          if (!line.mserp_alg_linedisc || line.mserp_alg_linedisc.trim() === '') {
            linesToUpdate.push(index)
          }
        })
      } else {
        // Apply to lines with specific Line Disc value
        console.log('Applying LinePercent:', percentValue, 'to all lines with Line Disc:', selectedLineDisc)
        editedLines.forEach((line, index) => {
          if (line.mserp_alg_linedisc === selectedLineDisc) {
            linesToUpdate.push(index)
          }
        })
      }
    } else {
      // Apply to selected lines only
      if (selectedLines.size === 0) return

      console.log('Applying bulk LinePercent:', percentValue, 'to selected lines:', Array.from(selectedLines))
      linesToUpdate = Array.from(selectedLines)
    }

    // Update all target lines with the new LinePercent value
    const newEditedLines = [...editedLines]
    const newModifiedFields = new Set(modifiedFields)

    linesToUpdate.forEach(index => {
      if (newEditedLines[index]) {
        // Get the sales price for THIS specific line
        const salesPrice = newEditedLines[index].mserp_salesprice || 0

        newEditedLines[index].mserp_linediscountpercentage = percentValue

        // Mark the field as modified for this line
        const fieldKey = `${index}-mserp_linediscountpercentage`
        newModifiedFields.add(fieldKey)

        // Calculate the new net from the current ERP price, or from the
        // fixed default net for lines already customized in the app.
        const productType = newEditedLines[index].mserp_alg_producttype

        const defaultNetSalesPrice = newEditedLines[index].mserp_alg_defaultnetsalesprice || 0
        const baseNetSalesPrice = salesPrice > 0 ? salesPrice : defaultNetSalesPrice
        if (baseNetSalesPrice > 0) {
          const newNetSalesPrice = baseNetSalesPrice * (1 - percentValue / 100)
          newEditedLines[index].mserp_alg_netsalesprice = Math.round(newNetSalesPrice * 100) / 100
          const netFieldKey = `${index}-mserp_alg_netsalesprice`
          newModifiedFields.add(netFieldKey)

          if (productType === '200000001' || productType === 200000001) {
            newEditedLines[index].mserp_alg_variationpercentage = percentValue
          } else if (defaultNetSalesPrice > 0) {
            const variationPercent = ((defaultNetSalesPrice - newNetSalesPrice) / defaultNetSalesPrice) * 100
            newEditedLines[index].mserp_alg_variationpercentage = Math.round(variationPercent * 100) / 100
          }
          const variationFieldKey = `${index}-mserp_alg_variationpercentage`
          newModifiedFields.add(variationFieldKey)

          // Recalculate LineAmount = Requested Qty × ALG_NetSalesPrice (same as individual edit)
          const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
          const lineAmount = calculateLineAmount(requestedQty, newNetSalesPrice)
          newEditedLines[index] = { ...newEditedLines[index], mserp_lineamount: lineAmount }
          const lineAmountFieldKey = `${index}-mserp_lineamount`
          newModifiedFields.add(lineAmountFieldKey)
        }
      }
    })

    // Update state with all changes
    setEditedLines(newEditedLines)
    setModifiedFields(newModifiedFields)
    setShowLinePercentModal(false)
    setBulkLinePercent('')
    setApplyByLineDisc(false)
    setSelectedLineDisc('')
    if (!applyByLineDisc) {
      setSelectedLines(new Set()) // Clear selection after bulk edit (only when applying by selection)
    }
  }

  const handleCancelBulkLinePercent = () => {
    setShowLinePercentModal(false)
    setBulkLinePercent('')
    setApplyByLineDisc(false)
    setSelectedLineDisc('')
  }

  // Helper function to get unique Line Disc values from all lines
  const getUniqueLineDiscValues = () => {
    const uniqueValues = new Set()
    editedLines.forEach(line => {
      if (line.mserp_alg_linedisc) {
        uniqueValues.add(line.mserp_alg_linedisc)
      }
    })
    return Array.from(uniqueValues).sort()
  }

  // Helper function to check if any selected lines have grouping
  const hasSelectedLinesWithGrouping = () => {
    return Array.from(selectedLines).some(index => {
      const line = editedLines[index]
      return line && line.mserp_alg_grouping && line.mserp_alg_grouping.trim() !== ''
    })
  }

  const isFieldModified = (index, field) => {
    const fieldKey = `${index}-${field}`
    return modifiedFields.has(fieldKey)
  }

  const isQuotationFieldModified = (field) => {
    return quotationModifiedFields.has(field)
  }

  const isQuotationReadOnly = () => {
    return quotationDetails?.mserp_alg_portalstatus !== 200000000
  }

  // Helper function to filter out 0-value fields from line data before sending to API
  const filterZeroValueFields = (lineData, context = '') => {
    const fieldsToFilter = ['mserp_salesprice', 'mserp_salespricequantity', 'mserp_lineamount', 'mserp_linediscountpercentage']
    const filteredLine = { ...lineData }
    
    fieldsToFilter.forEach(field => {
      if (filteredLine[field] === 0 || filteredLine[field] === '0' || filteredLine[field] === null || filteredLine[field] === undefined) {
        console.log(`Filtering out 0/null/undefined field: ${field} from ${context} (value: ${filteredLine[field]})`)
        delete filteredLine[field]
      }
    })
    
    return filteredLine
  }

  // Helper function to calculate ALG_DefaultNetSalesPrice = SalesPrice x (1-LinePercent/100)
  const calculateDefaultNetSalesPrice = (salesPrice, linePercent) => {
    if (!salesPrice || salesPrice <= 0) return 0
    
    const discount = linePercent || 0
    const calculated = salesPrice * (1 - discount / 100)
    return Math.round(calculated * 100) / 100 // Round to 2 decimal places
  }

  const prepareLinesForEditing = (sourceLines) => {
    const modifiedFields = new Set()
    const preparedLines = sourceLines.map((line, index) => {
      const preparedLine = { ...line }
      const salesPrice = Number(preparedLine.mserp_salesprice)
      const linePercent = Number(preparedLine.mserp_linediscountpercentage) || 0
      const defaultNetSalesPrice = Number(preparedLine.mserp_alg_defaultnetsalesprice)
      const currentNetSalesPrice = Number(preparedLine.mserp_alg_netsalesprice)

      if (Number.isFinite(currentNetSalesPrice) && currentNetSalesPrice > 0) {
        return preparedLine
      }

      const hasDefaultNetSalesPrice = Number.isFinite(defaultNetSalesPrice) && defaultNetSalesPrice > 0
      const hasSalesPrice = Number.isFinite(salesPrice) && salesPrice > 0
      if (!hasDefaultNetSalesPrice && !hasSalesPrice) return preparedLine

      const netSalesPrice = hasDefaultNetSalesPrice
        ? defaultNetSalesPrice
        : calculateDefaultNetSalesPrice(salesPrice, linePercent)

      if (!hasDefaultNetSalesPrice) {
        preparedLine.mserp_alg_defaultnetsalesprice = netSalesPrice
        modifiedFields.add(`${index}-mserp_alg_defaultnetsalesprice`)
      }

      preparedLine.mserp_alg_netsalesprice = netSalesPrice
      modifiedFields.add(`${index}-mserp_alg_netsalesprice`)

      const requestedQuantity = Number(preparedLine.mserp_requestedsalesquantity) || 0
      if (requestedQuantity > 0) {
        preparedLine.mserp_lineamount = calculateLineAmount(requestedQuantity, netSalesPrice)
        modifiedFields.add(`${index}-mserp_lineamount`)
      }

      return preparedLine
    })

    return { lines: preparedLines, modifiedFields }
  }

  // Helper function to calculate ALG_VariationPercentage = (ALG_DefaultNetSalesPrice - ALG_NetSalesPrice) / ALG_DefaultNetSalesPrice
  const calculateVariationPercentage = (defaultNetSalesPrice, netSalesPrice) => {
    if (!defaultNetSalesPrice || defaultNetSalesPrice <= 0) return 0
    
    const netPrice = netSalesPrice || 0
    const variation = (defaultNetSalesPrice - netPrice) / defaultNetSalesPrice
    return Math.round(variation * 10000) / 100 // Convert to percentage and round to 2 decimal places
  }

  // Helper function to calculate LineAmount = Requested Qty × ALG_NetSalesPrice
  const calculateLineAmount = (requestedQty, netSalesPrice) => {
    const qty = requestedQty || 0
    const price = netSalesPrice || 0

    const calculated = qty * price
    return Math.round(calculated * 100) / 100 // Round to 2 decimal places
  }

  const getQuantityValidation = (line) => {
    if (!line?._orderQuantitySettings) return null
    return validateOrderQuantity(line.mserp_requestedsalesquantity, line._orderQuantitySettings)
  }

  // Helper function to update ALG_VariationPercentage for a line
  const updateVariationPercentage = (lineData, lineIndex, context = '') => {
    const defaultNetSalesPrice = lineData.mserp_alg_defaultnetsalesprice || 0
    const netSalesPrice = lineData.mserp_alg_netsalesprice || 0
    
    if (defaultNetSalesPrice > 0) {
      const variationPercent = calculateVariationPercentage(defaultNetSalesPrice, netSalesPrice)
      lineData.mserp_alg_variationpercentage = variationPercent
      console.log(`Updated ALG_VariationPercentage ${context}: (${defaultNetSalesPrice} - ${netSalesPrice}) / ${defaultNetSalesPrice} = ${variationPercent}%`)
      return variationPercent
    }
    return 0
  }

  const {
    importErrors,
    importSummary,
    importInputRef,
    handleImportFile,
    downloadImportTemplate,
    getImportColumnLabel
  } = useQuotationLinesImport({
    t,
    quotationNumber,
    quotationDetails,
    lines,
    editedLines,
    isEditMode,
    smmParameters,
    setEditedLines,
    setModifiedFields,
    setIsEditMode,
    calculateDefaultNetSalesPrice,
    calculateVariationPercentage,
    calculateLineAmount
  })

  const hasUnsavedChanges = modifiedFields.size > 0 || deletedLines.length > 0 || editedLines.some(line => line._isNew) || quotationModifiedFields.size > 0
  
  // Debug logging for save button visibility
  React.useEffect(() => {
    console.log('=== SAVE BUTTON DEBUG ===')
    console.log('Modified fields count:', modifiedFields.size)
    console.log('Modified fields:', Array.from(modifiedFields))
    console.log('Deleted lines count:', deletedLines.length)
    console.log('Deleted lines:', deletedLines)
    console.log('New lines count:', editedLines.filter(line => line._isNew).length)
    console.log('Quotation modified fields:', Array.from(quotationModifiedFields))
    console.log('Has unsaved changes:', hasUnsavedChanges)
    console.log('Is edit mode:', isEditMode)
    console.log('========================')
  }, [modifiedFields, deletedLines, editedLines, quotationModifiedFields, hasUnsavedChanges, isEditMode])

  const getSalesStatusLabel = (statusValue) => {
    // Handle both string and numeric portal status values
    if (typeof statusValue === 'string') {
      const portalStatusMap = {
        'Draft': t('common:status.draft'),
        'SentToFnO': t('common:status.sentToErp'),
        'Sent': t('common:status.sent'),
        'Accepted': t('common:status.accepted'),
        'Rejected': t('common:status.rejected'),
        'Cancelled': t('common:status.cancelled')
      }
      return portalStatusMap[statusValue] || statusValue || t('common:status.draft')
    }

    // Map numeric values to portal status labels based on the image provided
    const statusMap = {
      200000000: t('common:status.draft'),
      200000001: t('common:status.sentToErp'),
      200000002: t('common:status.sent'),
      200000003: t('common:status.accepted'),
      200000004: t('common:status.rejected'),
      200000005: t('common:status.cancelled')
    }
    return statusMap[statusValue] || t('common:status.draft')
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const modalContent = (
    <div 
      className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center" 
      style={{
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-brand-contentBg rounded-lg shadow-lg w-[98vw] h-[95vh] overflow-hidden flex flex-col border border-gray-300"
      >
        <div className="p-6 border-b border-gray-300 bg-brand-headerBg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-brand-headerText">
                {t('quotation:lines.title', { number: quotationNumber })}
              </h2>
            </div>
            <div className="flex items-center space-x-3">
              {console.log('Rendering header - hasUnsavedChanges:', hasUnsavedChanges)}
              {hasUnsavedChanges && (
                <Button 
                  variant="success"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log('Save button clicked!')
                    console.log('Button event:', e)
                    console.log('hasUnsavedChanges:', hasUnsavedChanges)
                    console.log('loading:', loading)
                    handleSaveChanges()
                  }}
                  disabled={loading || getInvalidOrderQuantityLines(editedLines).length > 0}
                >
                  💾 {t('quotation:lines.saveChanges')}
                </Button>
              )}
              {isEditMode ? (
                <>
                  <Button
                    onClick={handleCancelChanges}
                    className="!bg-gray-100 hover:!bg-gray-200 !text-gray-700 !border !border-gray-300 px-4 py-2 rounded"
                  >
                    {t('common:actions.cancel')}
                  </Button>
                </>
              ) : (
                <>
                  {quotationDetails?.mserp_alg_portalstatus === 200000000 && lines.length > 0 && (
                    <Button
                      variant="infoLight"
                      onClick={handleSendToFnO}
                      className="mr-2"
                      disabled={loading}
                    >
                      {t('quotation:lines.sendToErp')}
                    </Button>
                  )}
                  {(quotationDetails?.mserp_alg_portalstatus === 200000001 || quotationDetails?.mserp_alg_portalstatus === 200000002) && (
                    <Button
                      variant="neutral"
                      onClick={handleChangeToDraft}
                      className="mr-2"
                      disabled={loading}
                    >
                      {t('quotation:lines.changeToDraft')}
                    </Button>
                  )}
                  {quotationDetails?.mserp_alg_portalstatus === 200000001 && (
                    <Button
                      variant="info"
                      onClick={handleRefreshQuotationHeader}
                      className="mr-2"
                      disabled={loading}
                      title={t('quotation:lines.refreshTitle')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                      {t('common:actions.refresh')}
                    </Button>
                  )}
                  {(quotationDetails?.mserp_alg_portalstatus === 200000001 || quotationDetails?.mserp_alg_portalstatus === 200000002) && (
                    <>
                      <Button
                        variant="success"
                        onClick={handleAcceptQuotation}
                        className="mr-2"
                        disabled={loading || (!quotationDetails?.mserp_requestingcustomeraccountnumber && quotationDetails?.mserp_prospectid)}
                        title={(!quotationDetails?.mserp_requestingcustomeraccountnumber && quotationDetails?.mserp_prospectid) ? t('quotation:lines.cannotAcceptProspect') : ''}
                      >
                        {t('quotation:lines.accept')}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={handleRejectQuotation}
                        className="mr-2"
                        disabled={loading}
                      >
                        {t('quotation:lines.reject')}
                      </Button>
                      <Button
                        variant="neutral"
                        onClick={handleCancelQuotation}
                        className="mr-2"
                        disabled={loading}
                      >
                        {t('common:actions.cancel')}
                      </Button>
                    </>
                  )}
                  {!isQuotationReadOnly() && (
                    <Button
                      variant="outline"
                      onClick={handleEditToggle}
                    >
                      {t('common:actions.edit')}
                    </Button>
                  )}
                </>
              )}

              {/* Report URL Button - hide while the quotation is in Draft */}
              {quotationDetails?.mserp_alg_salesquotationreporturl &&
               quotationDetails.mserp_alg_portalstatus !== 200000000 && (
                <Button
                  variant="report"
                  onClick={() => {
                    const baseUrl = 'https://spvg.sharepoint.com/sites/FO_DOCS/Shared%20Documents/'
                    const fullUrl = baseUrl + quotationDetails.mserp_alg_salesquotationreporturl
                    window.open(fullUrl, '_blank')
                  }}
                  className="mr-2"
                  title={t('quotation:lines.reportTitle')}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('quotation:lines.report')}
                </Button>
              )}

              {!standalone && (
                <Button
                  onClick={onClose}
                  className="!bg-gray-100 hover:!bg-gray-200 !text-gray-700 !border !border-gray-300 px-4 py-2 rounded"
                >
                  ✕
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Quotation Information Block */}
          <div className="bg-white border border-gray-300 rounded-sm mb-6">
            <button 
              onClick={() => setIsQuotationInfoExpanded(!isQuotationInfoExpanded)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-lg font-bold text-brand-darkText">{t('quotation:lines.quotationInfo')}</h3>
              <span className={`transform transition-transform duration-200 text-gray-500 ${isQuotationInfoExpanded ? 'rotate-180' : 'rotate-0'}`}>
                ▼
              </span>
            </button>
            <AnimatePresence>
              {isQuotationInfoExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="p-4 pt-0 max-h-[30vh] overflow-y-auto">
                    {quotationDetails ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                        {/* Column 1 */}
                        <div className="space-y-4 overflow-visible">
                          <div>
                            <label className="font-medium text-gray-600">{t('common:labels.quotationNumber')}:</label>
                            <p className="text-brand-darkText">{quotationDetails.mserp_salesquotationnumber || ''}</p>
                          </div>
                          <div>
                            <label className="font-medium text-gray-600">{t('common:labels.quotationName')}:</label>
                            {isEditMode ? (
                              <input
                                type="text"
                                value={editedQuotationDetails?.mserp_alg_salesquotationname || editedQuotationDetails?.mserp_salesquotationname || ''}
                                onChange={(e) => handleQuotationFieldEdit('mserp_alg_salesquotationname', e.target.value)}
                                className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isQuotationFieldModified('mserp_alg_salesquotationname') ? 'text-brand-primary' : ''}`}
                                placeholder={t('quotation:lines.enterQuotationName')}
                              />
                            ) : (
                              <p className={`text-brand-darkText ${isQuotationFieldModified('mserp_alg_salesquotationname') ? 'text-brand-primary' : ''}`}>
                                {quotationDetails.mserp_alg_salesquotationname || quotationDetails.mserp_salesquotationname || ''}
                              </p>
                            )}
                          </div>
                          {isSalutationCatalogEnabled && (
                            <div>
                              <label className="font-medium text-gray-600">{t('common:labels.salutation')}:</label>
                              {isEditMode ? (
                                <select
                                  value={editedQuotationDetails?.mserp_alg_salutation || ''}
                                  onChange={(e) => handleQuotationFieldEdit('mserp_alg_salutation', e.target.value)}
                                  className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isQuotationFieldModified('mserp_alg_salutation') ? 'text-brand-primary' : ''}`}
                                >
                                  <option value="">—</option>
                                  {editedQuotationDetails?.mserp_alg_salutation && !salutations.some(
                                    salutation => salutation.mserp_alg_salutationtypeid === editedQuotationDetails.mserp_alg_salutation
                                  ) && (
                                    <option value={editedQuotationDetails.mserp_alg_salutation}>
                                      {editedQuotationDetails.mserp_alg_salutation}
                                    </option>
                                  )}
                                  {salutations.filter(salutation => salutation.mserp_alg_salutationtypeid).map((salutation) => (
                                    <option
                                      key={salutation.mserp_alg_salutationtypeid}
                                      value={salutation.mserp_alg_salutationtypeid}
                                    >
                                      {salutation.mserp_alg_salutationtypeid}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-brand-darkText">{quotationDetails.mserp_alg_salutation || ''}</p>
                              )}
                            </div>
                          )}
                          {(isEditMode && quotationDetails.mserp_alg_portalstatus === 200000000 && quotationDetails.mserp_salesquotationstatus === 200000000) || quotationDetails.mserp_requestingcustomeraccountnumber ? (
                            <div className="relative">
                              {isEditMode && quotationDetails.mserp_alg_portalstatus === 200000000 && quotationDetails.mserp_salesquotationstatus === 200000000 ? (
                                <CustomerLookup
                                  label={`${t('common:labels.customerAccount')}:`}
                                  value={editedQuotationDetails?.mserp_requestingcustomeraccountnumber || ''}
                                  onChange={(customerAccount) => handleQuotationFieldEdit('mserp_requestingcustomeraccountnumber', customerAccount)}
                                  className={isQuotationFieldModified('mserp_requestingcustomeraccountnumber') ? 'ring-2 ring-brand-primary' : ''}
                                />
                              ) : (
                                <div>
                                  <label className="font-medium text-gray-600">{t('common:labels.customerAccount')}:</label>
                                  <p className={`text-brand-darkText ${isQuotationFieldModified('mserp_requestingcustomeraccountnumber') ? 'text-brand-primary' : ''}`}>
                                    {quotationDetails.mserp_requestingcustomeraccountnumber}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : null}
                          {(isEditMode && quotationDetails.mserp_alg_portalstatus === 200000000 && quotationDetails.mserp_salesquotationstatus === 200000000) || quotationDetails.mserp_prospectid ? (
                            <div className="relative">
                              {isEditMode && quotationDetails.mserp_alg_portalstatus === 200000000 && quotationDetails.mserp_salesquotationstatus === 200000000 ? (
                                <ProspectLookup
                                  label={`${t('common:labels.prospectId')}:`}
                                  value={editedQuotationDetails?.mserp_prospectid || ''}
                                  onChange={(prospectId) => handleQuotationFieldEdit('mserp_prospectid', prospectId)}
                                  className={isQuotationFieldModified('mserp_prospectid') ? 'ring-2 ring-brand-primary' : ''}
                                />
                              ) : (
                                <div>
                                  <label className="font-medium text-gray-600">{t('common:labels.prospectId')}:</label>
                                  <p className={`text-brand-darkText ${isQuotationFieldModified('mserp_prospectid') ? 'text-brand-primary' : ''}`}>
                                    {quotationDetails.mserp_prospectid}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : null}
                          {quotationDetails.mserp_alg_salesportalcontactname && (
                            <div>
                              <label className="font-medium text-gray-600">{t('common:labels.contactName')}:</label>
                              <p className="text-brand-darkText">{quotationDetails.mserp_alg_salesportalcontactname}</p>
                            </div>
                          )}
                          {quotationDetails.mserp_alg_salesportalcontactemail && (
                            <div>
                              <label className="font-medium text-gray-600">{t('common:labels.contactEmail')}:</label>
                              <p className="text-brand-darkText">{quotationDetails.mserp_alg_salesportalcontactemail}</p>
                            </div>
                          )}
                          <div>
                            <label className="font-medium text-gray-600">{t('common:labels.design')}:</label>
                            {isEditMode ? (
                              <DesignDropdown
                                value={editedQuotationDetails?.mserp_alg_designname || ''}
                                onChange={(designCode) => handleQuotationFieldEdit('mserp_alg_designname', designCode)}
                                className={isQuotationFieldModified('mserp_alg_designname') ? 'ring-2 ring-brand-primary' : ''}
                              />
                            ) : (
                              <p className={`text-brand-darkText ${isQuotationFieldModified('mserp_alg_designname') ? 'text-brand-primary' : ''}`}>
                                {quotationDetails.mserp_alg_designname || '-'}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Column 2 */}
                        <div className="space-y-4">
                          <div>
                            <label className="font-medium text-gray-600">{t('common:labels.portalStatus')}:</label>
                            <p className={`font-medium ${
                              quotationDetails.mserp_alg_portalstatus === 200000000 ? 'text-brand-darkText' : // Draft - normal text
                              quotationDetails.mserp_alg_portalstatus === 200000001 || quotationDetails.mserp_alg_portalstatus === 200000002 ? 'text-blue-600' : // Sent to ERP or Sent - blue
                              quotationDetails.mserp_alg_portalstatus === 200000003 ? 'text-green-600' : // Accepted - green
                              quotationDetails.mserp_alg_portalstatus === 200000004 || quotationDetails.mserp_alg_portalstatus === 200000005 ? 'text-red-600' : // Rejected or Cancelled - red
                              'text-brand-darkText' // fallback
                            }`}>
                              {getSalesStatusLabel(quotationDetails.mserp_alg_portalstatus)}
                            </p>
                            {/* Removed read-only label as requested */}
                          </div>
                          <div>
                            <label className="font-medium text-gray-600">{t('common:labels.opportunityId')}:</label>
                            {isEditMode &&
                             quotationDetails.mserp_alg_portalstatus === 200000000 &&
                             smmParameters?.mserp_alg_allowopportunityfield === 200000001 ? (
                              <input
                                type="text"
                                value={editedQuotationDetails?.mserp_alg_portalopportunity || ''}
                                onChange={(e) => handleQuotationFieldEdit('mserp_alg_portalopportunity', e.target.value)}
                                className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isQuotationFieldModified('mserp_alg_portalopportunity') ? 'text-brand-primary' : ''}`}
                                placeholder={t('quotation:lines.enterOpportunityId')}
                                maxLength={50}
                              />
                            ) : (
                              <p className={`text-brand-darkText ${isQuotationFieldModified('mserp_alg_portalopportunity') ? 'text-brand-primary' : ''}`}>
                                {quotationDetails.mserp_alg_portalopportunity || ''}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="font-medium text-gray-600">{t('common:labels.followUpDate')}:</label>
                            {isEditMode ? (
                              <input
                                type="date"
                                value={editedQuotationDetails?.mserp_salesquotationfollowupdate ? editedQuotationDetails.mserp_salesquotationfollowupdate.substring(0, 10) : ''}
                                onChange={(e) => handleQuotationFieldEdit('mserp_salesquotationfollowupdate', e.target.value ? `${e.target.value}T00:00:00Z` : null)}
                                className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isQuotationFieldModified('mserp_salesquotationfollowupdate') ? 'text-brand-primary' : ''}`}
                              />
                            ) : (
                              <p className={`text-brand-darkText ${isQuotationFieldModified('mserp_salesquotationfollowupdate') ? 'text-brand-primary' : ''}`}>
                                {formatDate(quotationDetails.mserp_salesquotationfollowupdate)}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="font-medium text-gray-600">{t('common:labels.expiryDate')}:</label>
                            {isEditMode ? (
                              <input
                                type="date"
                                value={editedQuotationDetails?.mserp_salesquotationexpirydate ? editedQuotationDetails.mserp_salesquotationexpirydate.substring(0, 10) : ''}
                                onChange={(e) => handleQuotationFieldEdit('mserp_salesquotationexpirydate', e.target.value ? e.target.value + 'T00:00:00Z' : null)}
                                className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isQuotationFieldModified('mserp_salesquotationexpirydate') ? 'text-brand-primary' : ''}`}
                              />
                            ) : (
                              <p className={`text-brand-darkText ${isQuotationFieldModified('mserp_salesquotationexpirydate') ? 'text-brand-primary' : ''}`}>
                                {formatDate(quotationDetails.mserp_salesquotationexpirydate)}
                              </p>
                            )}
                          </div>
                          {/* Creation date. Always read-only: F&O stamps mserp_quote_createddatetime
                              on creation and Dataverse rejects it for create/update. */}
                          {quotationDetails?.mserp_quote_createddatetime && (
                            <div>
                              <label className="font-medium text-gray-600">{t('common:labels.createdDate')}:</label>
                              <p className="text-brand-darkText">
                                {formatDate(quotationDetails.mserp_quote_createddatetime)}
                              </p>
                            </div>
                          )}
                        </div>

                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary mx-auto"></div>
                        <p className="text-sm text-gray-600 mt-2">{t('quotation:lines.loadingDetails')}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-brand-darkText">{t('quotation:lines.loadingLines')}</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="text-red-500">⚠️</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-red-800 font-medium mb-1">{t('quotation:lines.saveFailed')}</h3>
                  <p className="text-red-700 text-sm">{error}</p>
                  {error.includes('dimension') && (
                    <div className="mt-2 text-xs text-red-600">
                      <p><strong>{t('quotation:lines.dimensionTip')}</strong></p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {warning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-700">
                <span className="font-semibold">⚠️ Warning:</span> {warning}
              </p>
            </div>
          )}

          {(importSummary || importErrors.length > 0) && (
            <div className={`rounded-lg border p-4 mb-4 ${importErrors.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              {importSummary && (
                <p className="text-sm font-medium text-gray-800">
                  {t('quotation:lines.import.summary', {
                    fileName: importSummary.fileName,
                    imported: importSummary.imported,
                    rejected: importSummary.rejected
                  })}
                </p>
              )}
              {importErrors.length > 0 && (
                <div className="mt-3 text-sm text-amber-900">
                  <p className="font-semibold">{t('quotation:lines.import.errorTitle')}</p>
                  <ul className="mt-1 space-y-1 max-h-32 overflow-y-auto list-disc list-inside">
                    {importErrors.map((rowError, index) => (
                      <li key={`${rowError.rowNumber}-${index}`}>
                        <span className="font-medium">{t('quotation:lines.import.row', { row: rowError.rowNumber })}:</span>{' '}
                        {rowError.errors.map(errorItem => `${getImportColumnLabel(errorItem.field)}: ${errorItem.message}`).join('; ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {importSummary?.imported > 0 && (
                <p className="mt-2 text-xs text-green-800">{t('quotation:lines.import.saveHint')}</p>
              )}
            </div>
          )}

          {/* View Selector and Action Buttons - combined row */}
          {!loading && (lines.length > 0 || editedLines.length > 0 || isEditMode) && (
            <div className="flex justify-between items-center mb-4">
              {/* Left side - View Selector */}
                   <div className="flex items-center gap-3">
                <div className="relative" ref={dropdownRef}>
                  {/* Dropdown Button */}
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setIsDropdownOpen(!isDropdownOpen)
                      } else if (e.key === 'Escape') {
                        setIsDropdownOpen(false)
                      }
                    }}
                    className="flex items-center justify-between px-4 py-2 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-brand-primary hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 min-w-[180px]"
                    aria-haspopup="listbox"
                    aria-expanded={isDropdownOpen}
                    aria-label="Select view type"
                  >
                    <span className="text-left">
                      {currentView === 'lines' ? t('quotation:lines.views.linesOfQuote') : t('quotation:lines.views.allFields')}
                    </span>
                    <svg
                      className={`ml-2 h-4 w-4 text-gray-500 transition-transform duration-200 ${
                        isDropdownOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                      <div
                        role="listbox"
                        aria-label="View options"
                        className="py-1"
                      >
                        {/* Lines of Quote Option */}
                        <button
                          type="button"
                          role="option"
                          aria-selected={currentView === 'lines'}
                          onClick={() => {
                            setCurrentView('lines')
                            setIsDropdownOpen(false)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              setCurrentView('lines')
                              setIsDropdownOpen(false)
                            }
                          }}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors duration-150 ${
                            currentView === 'lines'
                              ? 'bg-brand-primary text-white font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          } focus:outline-none focus:bg-brand-primary focus:text-white`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{t('quotation:lines.views.linesOfQuote')}</span>
                            {currentView === 'lines' && (
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="text-xs text-current opacity-75 mt-1">
                            {t('quotation:lines.views.focusedView')}
                          </div>
                        </button>

                        {/* Separator */}
                        <div className="border-t border-gray-100"></div>

                        {/* All fields Option */}
                        <button
                          type="button"
                          role="option"
                          aria-selected={currentView === 'all'}
                          onClick={() => {
                            setCurrentView('all')
                            setIsDropdownOpen(false)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              setCurrentView('all')
                              setIsDropdownOpen(false)
                            }
                          }}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors duration-150 ${
                            currentView === 'all'
                              ? 'bg-brand-primary text-white font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          } focus:outline-none focus:bg-brand-primary focus:text-white`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{t('quotation:lines.views.allFields')}</span>
                            {currentView === 'all' && (
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="text-xs text-current opacity-75 mt-1">
                            {t('quotation:lines.views.completeView')}
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Action Buttons (only show in edit mode) */}
              {isEditMode && (
                <div className="flex items-center gap-3">
                  {selectedLines.size > 0 && (
                    <div className="flex items-center gap-2">
                      {/* Group Button */}
                      <button
                        type="button"
                        onClick={handleGroupSelectedLines}
                        disabled={isQuotationReadOnly()}
                        className={`
                          inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2
                          text-sm font-medium rounded-md transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500
                          ${isQuotationReadOnly()
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800 hover:shadow-md active:shadow-sm'
                          }
                        `}
                        aria-label={isQuotationReadOnly() ? "Cannot group - quotation is not in Draft status" : "Group selected lines"}
                        title={isQuotationReadOnly() ? "Cannot group - quotation is not in Draft status" : "Group selected lines"}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        {t('quotation:lines.actions.group')}
                      </button>

                      {/* Ungroup Button - only show if selected lines have grouping */}
                      {hasSelectedLinesWithGrouping() && (
                        <button
                          type="button"
                          onClick={handleUngroupSelectedLines}
                          disabled={isQuotationReadOnly()}
                          className={`
                            inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2
                            text-sm font-medium rounded-md transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                            ${isQuotationReadOnly()
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 hover:shadow-md active:shadow-sm'
                            }
                          `}
                          aria-label={isQuotationReadOnly() ? "Cannot ungroup - quotation is not in Draft status" : "Ungroup selected lines"}
                          title={isQuotationReadOnly() ? "Cannot ungroup - quotation is not in Draft status" : "Ungroup selected lines"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          {t('quotation:lines.actions.ungroup')}
                        </button>
                      )}

                      {/* Duplicate Button */}
                      <button
                        type="button"
                        onClick={handleDuplicateSelectedLines}
                        disabled={isQuotationReadOnly()}
                        className={`
                          inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2
                          text-sm font-medium rounded-md transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500
                          ${isQuotationReadOnly()
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 hover:shadow-md active:shadow-sm'
                          }
                        `}
                        aria-label={isQuotationReadOnly() ? "Cannot duplicate - quotation is not in Draft status" : "Duplicate selected lines"}
                        title={isQuotationReadOnly() ? "Cannot duplicate - quotation is not in Draft status" : "Duplicate selected lines"}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {t('common:actions.duplicate')}
                      </button>

                      {/* Delete Button */}
                      <button
                        type="button"
                        onClick={handleDeleteSelectedLines}
                        disabled={isQuotationReadOnly()}
                        className={`
                          inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2
                          text-sm font-medium rounded-md transition-all duration-200
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                          ${isQuotationReadOnly()
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 hover:shadow-md active:shadow-sm'
                          }
                        `}
                        aria-label={isQuotationReadOnly() ? "Cannot delete - quotation is not in Draft status" : "Delete selected lines"}
                        title={isQuotationReadOnly() ? "Cannot delete - quotation is not in Draft status" : "Delete selected lines"}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {t('common:actions.delete')}
                      </button>
                    </div>
                  )}

                  {/* Edit Percent Button - always show in edit mode */}
                  <button
                    type="button"
                    onClick={handleBulkLinePercentEdit}
                    disabled={isQuotationReadOnly()}
                    className={`
                      inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2
                      text-sm font-medium rounded-md transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                      ${isQuotationReadOnly()
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 hover:shadow-md active:shadow-sm'
                      }
                    `}
                    aria-label={isQuotationReadOnly() ? "Cannot edit LinePercent - quotation is not in Draft status" : "Edit LinePercent"}
                    title={isQuotationReadOnly() ? "Cannot edit LinePercent - quotation is not in Draft status" : "Edit LinePercent"}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {t('quotation:lines.actions.editPercent')}
                  </button>

                   {/* Add New Line Button - always show in edit mode */}
                   <input
                     ref={importInputRef}
                     type="file"
                     accept=".xlsx,.xls,.csv"
                     onChange={handleImportFile}
                     className="hidden"
                   />
                   <button
                     type="button"
                     onClick={() => importInputRef.current?.click()}
                     disabled={isQuotationReadOnly()}
                     className={`inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isQuotationReadOnly() ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 hover:shadow-md'}`}
                     title={t('quotation:lines.import.importButton')}
                   >
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16V4m0 0L8 8m4-4l4 4M5 20h14a2 2 0 002-2v-3M3 15v3a2 2 0 002 2" />
                     </svg>
                     {t('quotation:lines.import.importButton')}
                   </button>
                   <button
                     type="button"
                     onClick={() => downloadImportTemplate('xlsx')}
                     disabled={isQuotationReadOnly()}
                     className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                     title={t('quotation:lines.import.templateButton')}
                   >
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M5 20h14" />
                     </svg>
                     {t('quotation:lines.import.templateButton')}
                   </button>
                   <button
                    type="button"
                    onClick={handleAddNewLine}
                    disabled={isQuotationReadOnly()}
                    className={`
                      inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2
                      text-sm font-medium rounded-md transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
                      ${isQuotationReadOnly()
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                        : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800 hover:shadow-md active:shadow-sm'
                      }
                    `}
                    aria-label={isQuotationReadOnly() ? "Cannot add lines - quotation is not in Draft status" : "Add new line"}
                    title={isQuotationReadOnly() ? "Cannot add lines - quotation is not in Draft status" : "Add new line"}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('quotation:lines.addLine')}
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && lines.length === 0 && editedLines.length === 0 && !error && (
            <div className="text-center py-8">
              <p className="text-brand-darkText mb-4">{t('quotation:lines.noLinesFound')}</p>
              {isEditMode && (
                <button
                  type="button"
                  onClick={handleAddNewLine}
                  disabled={isQuotationReadOnly()}
                  className={`
                    inline-flex items-center justify-center gap-2 px-6 py-3
                    text-sm font-medium rounded-md transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
                    ${isQuotationReadOnly()
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                      : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800 hover:shadow-md active:shadow-sm'
                    }
                  `}
                  aria-label={isQuotationReadOnly() ? "Cannot add lines - quotation is not in Draft status" : "Add first line"}
                  title={isQuotationReadOnly() ? "Cannot add lines - quotation is not in Draft status" : "Add first line"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('quotation:lines.addFirstLine')}
                </button>
              )}
            </div>
          )}

          {!loading && (lines.length > 0 || editedLines.length > 0) && (
            <>
              <div className="border border-gray-300 rounded-sm overflow-hidden flex-1 flex flex-col min-h-0">
                <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0">
                  <table className="w-full table-fixed" style={{ minWidth: currentView === 'all' ? '2800px' : '2640px' }}>
                  <thead className="bg-black sticky top-0 z-0">
                    <tr>
                      {isEditMode && (
                        <th className="px-1 py-2 text-center text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedLines.size > 0 && selectedLines.size === (isEditMode ? editedLines : lines).length}
                            onChange={(e) => handleSelectAllLines(e.target.checked)}
                            className="rounded"
                          />
                        </th>
                      )}
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '80px' }}>
                        {t('quotation:lines.columns.ord')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '130px' }}>
                        {t('quotation:lines.columns.grouping')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '180px' }}>
                        {t('quotation:lines.columns.itemNumber')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '280px' }}>
                        {t('quotation:lines.columns.description')}
                      </th>
                      {currentView === 'all' && (
                        <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '280px' }}>
                          {t('quotation:lines.columns.commercialDescription')}
                        </th>
                      )}
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '120px' }}>
                        {t('quotation:lines.columns.productType')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '120px' }}>
                        {t('quotation:lines.columns.outOfCatalog')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '100px' }}>
                        {t('quotation:lines.columns.lineDisc')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '130px' }}>
                        {t('quotation:lines.columns.requestedQty')}
                      </th>
                      <th className="px-1 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '110px' }}>
                        {t('quotation:lines.columns.salesPrice')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '80px' }}>
                        {t('quotation:lines.columns.discountPercent')}
                      </th>
                      <th className="px-1 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '80px' }}>
                        {t('quotation:lines.columns.variationPercent')}
                      </th>
                      {currentView === 'all' && (
                        <>
                          <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '160px' }}>
                            {t('quotation:lines.columns.itemRefEasyVent')}
                          </th>
                          <th className="px-1 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '120px' }}>
                            {t('quotation:lines.columns.externalItemNumber')}
                          </th>
                          <th className="px-1 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '80px' }}>
                            {t('quotation:lines.columns.flow')}
                          </th>
                          <th className="px-1 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '80px' }}>
                            {t('quotation:lines.columns.pressure')}
                          </th>
                           <th className="px-1 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '90px' }}>
                             {t('quotation:lines.columns.gradeRpm')}
                           </th>
                            <th className="px-1 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '105px' }}>
                             {t('quotation:lines.columns.defaultNetPrice')}
                          </th>
                        </>
                      )}
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '130px' }}>
                        {t('quotation:lines.columns.netSalesPrice')}
                      </th>
                      <th className="px-2 py-2 text-left text-sm font-bold text-white border-b border-gray-600 bg-black" style={{ width: '130px' }}>
                        {t('quotation:lines.columns.lineAmount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {(isEditMode ? editedLines : lines).map((line, index) => (
                      <motion.tr
                        key={line.mserp_alg_salesquotationlinev2entityid || line.mserp_salesquotationlinev2entityid || line.id || `line-${index}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        layout
                        transition={{ delay: index * 0.05 }}
                        onKeyDown={handleLineKeyDown}
                        draggable={isEditMode}
                        onDragStart={(event) => handleLineDragStart(event, index)}
                        onDragOver={(event) => handleLineDragOver(event, index)}
                        onDrop={(event) => handleLineDrop(event, index)}
                        onDragEnd={handleLineDragEnd}
                        className={`transition-colors duration-200 ${
                          failedLineIndexes.has(index) 
                            ? 'bg-red-50 hover:bg-red-100 border-l-4 border-red-500' 
                            : 'bg-white hover:bg-gray-50'
                        } ${isEditMode ? 'cursor-move' : ''} ${
                          draggedLineIndex === index ? 'opacity-40 scale-[0.99] shadow-lg' : ''
                        } ${
                          dragOverLineIndex === index && draggedLineIndex !== index
                            ? 'border-t-4 border-t-brand-primary bg-blue-50'
                            : ''
                        }`}
                      >
                        {isEditMode && (
                          <td className="px-1 py-2 text-center" style={{ width: '40px' }}>
                            <span
                              data-drag-handle
                              className="inline-block mr-1 text-gray-400 hover:text-brand-primary cursor-grab active:cursor-grabbing select-none"
                              title="Arrastrar línea para reordenar"
                              aria-label="Arrastrar línea para reordenar"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                <circle cx="5" cy="4" r="1" />
                                <circle cx="11" cy="4" r="1" />
                                <circle cx="5" cy="8" r="1" />
                                <circle cx="11" cy="8" r="1" />
                                <circle cx="5" cy="12" r="1" />
                                <circle cx="11" cy="12" r="1" />
                              </svg>
                            </span>
                            <input
                              type="checkbox"
                              checked={selectedLines.has(index)}
                              onChange={(e) => handleSelectLine(index, e.target.checked)}
                              className="rounded"
                            />
                          </td>
                        )}
                        <td className="px-2 py-2" style={{ width: '80px' }}>
                          {isEditMode ? (
                            <input
                              type="number"
                              name="line-display-order"
                              value={line.mserp_alg_reportdisplayorder || ''}
                              onChange={(e) => handleCellEdit(index, 'mserp_alg_reportdisplayorder', parseInt(e.target.value) || 0)}
                              onBlur={handleManualOrderCommit}
                              className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isFieldModified(index, 'mserp_alg_reportdisplayorder') ? 'text-brand-primary' : ''}`}
                              step="1"
                            />
                          ) : (
                            <span className={isFieldModified(index, 'mserp_alg_reportdisplayorder') ? 'text-brand-primary' : 'text-brand-darkText'}>
                              {line.mserp_alg_reportdisplayorder || ''}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2" style={{ width: '130px' }}>
                          <span className={`${isFieldModified(index, 'mserp_alg_grouping') ? 'text-brand-primary' : 'text-brand-darkText'} ${line.mserp_alg_grouping ? 'bg-gray-100 px-2 py-1 rounded text-sm' : ''}`}>
                            {line.mserp_alg_grouping || ''}
                          </span>
                        </td>
                        <td className="px-2 py-2 relative" style={{ width: '180px' }}>
                          {failedLineIndexes.has(index) && (
                            <div className="absolute left-1 top-1/2 transform -translate-y-1/2">
                              <span className="text-red-500 font-bold text-lg">✗</span>
                            </div>
                          )}
                          <div className={`${failedLineIndexes.has(index) ? 'ml-6' : ''}`}>
                            {isEditMode && line._isNew && line.mserp_alg_producttype !== '200000001' && line.mserp_alg_producttype !== 200000001 ? (
                              <ItemDropdown
                                value={line.mserp_itemnumber || ''}
                                onChange={(value) => handleCellEdit(index, 'mserp_itemnumber', value)}
                                onProductSelect={(product) => handleItemSelect(index, product)}
                                autoFocus={line.id === focusItemInputLineId}
                                onInputFocus={() => setFocusItemInputLineId(currentId => currentId === line.id ? null : currentId)}
                                className={isFieldModified(index, 'mserp_itemnumber') ? 'text-brand-primary' : ''}
                              />
                            ) : (
                              <span className={`font-medium ${isFieldModified(index, 'mserp_itemnumber') ? 'text-brand-primary' : 'text-brand-darkText'}`}>
                                {(line.mserp_alg_producttype === '200000001' || line.mserp_alg_producttype === 200000001) ? '' : (line.mserp_itemnumber || '')}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-2 py-2" style={{ width: '280px' }}>
                          <span
                            className="text-brand-darkText break-words"
                            title={line.mserp_productsearchname || ''}
                          >
                            {line.mserp_productsearchname || ''}
                          </span>
                        </td>

                        {currentView === 'all' && (
                          <td className="px-2 py-2" style={{ width: '280px' }}>
                            <span
                              className="text-gray-600 break-words"
                              title={commercialDescriptions[line.mserp_productnumber] || ''}
                            >
                              {commercialDescriptions[line.mserp_productnumber] || ''}
                            </span>
                          </td>
                        )}

                        <td className="px-2 py-2" style={{ width: '120px' }}>
                          {isEditMode && line._isNew ? (
                            <select
                              value={line.mserp_alg_producttype}
                              onChange={(e) => {
                                const newValue = e.target.value
                                console.log('Product type changing to:', newValue)

                                // Update fields in a single state update
                                const newEditedLines = [...editedLines]
                                // Get out of stock item number from SMM parameters, fallback to hardcoded value
                                const outOfStockItemNumber = smmParameters?.mserp_alg_productoutofcatalog || 'L999OutOfStock'

                                newEditedLines[index] = {
                                  ...newEditedLines[index],
                                  mserp_alg_producttype: newValue,
                                  // Set specific item number when switching to "Out of Stock"
                                  ...(newValue === '200000001' ? { mserp_itemnumber: outOfStockItemNumber } : {}),
                                  // Clear out of catalog field when switching to "Existing"
                                  ...(newValue === '200000000' || newValue === 200000000 ? { mserp_alg_outofcatalogproduct: '' } : {})
                                }

                                console.log(`Setting Out of Stock item number to: ${outOfStockItemNumber} (from SMM parameters)`)
                                setEditedLines(newEditedLines)

                                // Mark fields as modified
                                setModifiedFields(prev => {
                                  const newSet = new Set(prev)
                                  newSet.add(`${index}-mserp_alg_producttype`)
                                  if (newValue === '200000001') {
                                    newSet.add(`${index}-mserp_itemnumber`)
                                  }
                                  if (newValue === '200000000' || newValue === 200000000) {
                                    newSet.add(`${index}-mserp_alg_outofcatalogproduct`)
                                  }
                                  return newSet
                                })
                              }}
                              className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isFieldModified(index, 'mserp_alg_producttype') ? 'text-brand-primary' : ''}`}
                            >
                              <option value="200000000">{t('quotation:lines.productTypes.existing')}</option>
                              <option value="200000001">{t('quotation:lines.productTypes.outOfCatalog')}</option>
                            </select>
                          ) : (
                            <span className={isFieldModified(index, 'mserp_alg_producttype') ? 'text-brand-primary' : 'text-brand-darkText'}>
                              {line.mserp_alg_producttype === '200000000' || line.mserp_alg_producttype === 200000000 ? t('quotation:lines.productTypes.existing') :
                               line.mserp_alg_producttype === '200000001' || line.mserp_alg_producttype === 200000001 ? t('quotation:lines.productTypes.outOfCatalog') : ''}
                            </span>
                          )}
                        </td>

                        <td className="px-2 py-2" style={{ width: '120px' }}>
                          {isEditMode && (line.mserp_alg_producttype === '200000001' || line.mserp_alg_producttype === 200000001) ? (
                            <input
                              type="text"
                              value={line.mserp_alg_outofcatalogproduct || ''}
                              onChange={(e) => handleCellEdit(index, 'mserp_alg_outofcatalogproduct', e.target.value)}
                              className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isFieldModified(index, 'mserp_alg_outofcatalogproduct') ? 'text-brand-primary' : ''}`}
                            />
                          ) : (
                            <span className={isFieldModified(index, 'mserp_alg_outofcatalogproduct') ? 'text-brand-primary' : 'text-brand-darkText'}>
                              {(line.mserp_alg_producttype === '200000001' || line.mserp_alg_producttype === 200000001) ? (line.mserp_alg_outofcatalogproduct || '') : ''}
                            </span>
                          )}
                        </td>

                        <td className="px-2 py-2" style={{ width: '100px' }}>
                          <span className="text-brand-darkText">
                            {line.mserp_alg_linedisc || ''}
                          </span>
                        </td>

                        <td className="px-2 py-2" style={{ width: '130px' }}>
                          {isEditMode ? (
                            <div>
                              <input
                                type="number"
                                value={line.mserp_requestedsalesquantity || ''}
                                onChange={(e) => handleCellEdit(index, 'mserp_requestedsalesquantity', parseFloat(e.target.value) || 0)}
                                min={line._orderQuantitySettings?.minimumSalesOrderQuantity || undefined}
                                step={line._orderQuantitySettings?.salesQuantityMultiple || '0.01'}
                                aria-invalid={getQuantityValidation(line)?.isValid === false}
                                className={`w-full px-2 py-1 border rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${getQuantityValidation(line)?.isValid === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} ${isFieldModified(index, 'mserp_requestedsalesquantity') ? 'text-brand-primary' : ''}`}
                              />
                              {line._orderQuantityLoading && (
                                <span className="text-xs text-gray-500">{t('quotation:lines.loadingQuantitySettings')}</span>
                              )}
                              {line._orderQuantityError && (
                                <span className="text-xs text-red-600">{t('quotation:lines.quantitySettingsUnavailable')}</span>
                              )}
                              {getQuantityValidation(line)?.isValid === false && (
                                <span className="text-xs text-red-600">
                                  {t(getOrderQuantityErrorKey(getQuantityValidation(line)), getQuantityValidation(line))}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className={isFieldModified(index, 'mserp_requestedsalesquantity') ? 'text-brand-primary' : 'text-brand-darkText'}>
                              {formatQuantity(line.mserp_requestedsalesquantity)}
                            </span>
                          )}
                        </td>
                        
                        
                        <td className="px-1 py-2" style={{ width: '110px' }}>
                          {isEditMode && (line.mserp_alg_producttype === '200000001' || line.mserp_alg_producttype === 200000001) ? (
                            <input
                              type="number"
                              value={line.mserp_salesprice || ''}
                              onChange={(e) => {
                                const newSalesPrice = parseFloat(e.target.value) || 0

                                // For Out of Stock items, update Sales Price, Net Sales Price, ALG_VariationPercentage, and Line Amount
                                if (line.mserp_alg_producttype === '200000001' || line.mserp_alg_producttype === 200000001) {
                                  const newEditedLines = [...editedLines]
                                  const linePercent = newEditedLines[index].mserp_linediscountpercentage || 0

                                  // Calculate Net Sales Price = Sales Price × (1 - Discount%/100)
                                  // Even for Out of Catalog items, discount should be applied to Line Amount
                                  const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
                                  const netSalesPrice = newSalesPrice * (1 - linePercent / 100)
                                  const lineAmount = calculateLineAmount(requestedQty, netSalesPrice)

                                  newEditedLines[index] = {
                                    ...newEditedLines[index],
                                    mserp_salesprice: newSalesPrice,
                                    mserp_alg_netsalesprice: Math.round(netSalesPrice * 100) / 100,
                                    mserp_alg_variationpercentage: linePercent,
                                    mserp_lineamount: lineAmount
                                  }
                                  setEditedLines(newEditedLines)

                                  // Mark all four fields as modified
                                  setModifiedFields(prev => {
                                    const newSet = new Set(prev)
                                    newSet.add(`${index}-mserp_salesprice`)
                                    newSet.add(`${index}-mserp_alg_netsalesprice`)
                                    newSet.add(`${index}-mserp_alg_variationpercentage`)
                                    newSet.add(`${index}-mserp_lineamount`)
                                    return newSet
                                  })
                                } else {
                                  handleCellEdit(index, 'mserp_salesprice', newSalesPrice)
                                }
                              }}
                              className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isFieldModified(index, 'mserp_salesprice') ? 'text-brand-primary' : ''}`}
                              step="0.01"
                              min="0"
                            />
                          ) : (
                            <span className={isFieldModified(index, 'mserp_salesprice') ? 'text-brand-primary' : 'text-brand-darkText'}>
                              {formatCurrency(line.mserp_salesprice)}
                            </span>
                          )}
                        </td>
                        
                        <td className="px-2 py-2" style={{ width: '80px' }}>
                          {isEditMode ? (
                            <div className="relative">
                              <input
                                type="number"
                                value={line.mserp_linediscountpercentage || ''}
                                onChange={(e) => {
                                  let value = parseFloat(e.target.value) || 0
                                  // Clamp value between 0 and 100
                                  value = Math.max(0, Math.min(100, value))

                                  // For Out of Stock items, handle LinePercent change specially
                                  const productType = line.mserp_alg_producttype
                                  if (productType === '200000001' || productType === 200000001) {
                                    const newEditedLines = [...editedLines]
                                    const salesPrice = newEditedLines[index].mserp_salesprice || 0
                                    const netSalesPrice = salesPrice * (1 - value / 100)

                                    // Calculate Line Amount = Requested Qty × Net Sales Price
                                    const requestedQty = newEditedLines[index].mserp_requestedsalesquantity || 0
                                    const lineAmount = calculateLineAmount(requestedQty, netSalesPrice)

                                    newEditedLines[index] = {
                                      ...newEditedLines[index],
                                      mserp_linediscountpercentage: value,
                                      mserp_alg_netsalesprice: Math.round(netSalesPrice * 100) / 100,
                                      mserp_alg_variationpercentage: value, // Set variation to same as line percent
                                      mserp_lineamount: lineAmount // Add Line Amount calculation
                                    }
                                    setEditedLines(newEditedLines)

                                    // Mark fields as modified
                                    setModifiedFields(prev => {
                                      const newSet = new Set(prev)
                                      newSet.add(`${index}-mserp_linediscountpercentage`)
                                      newSet.add(`${index}-mserp_alg_netsalesprice`)
                                      newSet.add(`${index}-mserp_alg_variationpercentage`)
                                      newSet.add(`${index}-mserp_lineamount`) // Mark Line Amount as modified
                                      return newSet
                                    })
                                  } else {
                                    handleCellEdit(index, 'mserp_linediscountpercentage', value)
                                  }
                                }}
                                className={`min-w-[80px] w-full px-2 py-1 pr-6 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isFieldModified(index, 'mserp_linediscountpercentage') ? 'text-brand-primary' : ''}`}
                                step="0.01"
                                min="0"
                                max="100"
                              />
                              <span className="absolute right-0 top-1 text-gray-500 pointer-events-none">%</span>
                            </div>
                          ) : (
                            <span className={isFieldModified(index, 'mserp_linediscountpercentage') ? 'text-brand-primary' : 'text-brand-darkText'}>
                              {line.mserp_linediscountpercentage ? `${line.mserp_linediscountpercentage}%` : '0%'}
                            </span>
                          )}
                        </td>
                        
                        <td className="px-1 py-2" style={{ width: '80px' }}>
                          {/* ALG_VariationPercentage is always read-only */}
                          <span className="text-gray-600 italic" title="Read-only calculated field">
                            {line.mserp_alg_variationpercentage ? `${line.mserp_alg_variationpercentage}%` : '0%'}
                          </span>
                        </td>
                        
                        {currentView === 'all' && (
                          <>
                            <td className="px-2 py-2" style={{ width: '160px' }}>
                              <span className="text-brand-darkText">
                                {line.mserp_alg_itemreferenceeasyvent || ''}
                              </span>
                            </td>

                            <td className="px-1 py-2" style={{ width: '120px' }}>
                              {isEditMode ? (
                                <input
                                  type="text"
                                  value={line.mserp_externalitemnumber || ''}
                                  onChange={(e) => handleCellEdit(index, 'mserp_externalitemnumber', e.target.value)}
                                  className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isFieldModified(index, 'mserp_externalitemnumber') ? 'text-brand-primary' : ''}`}
                                  title="External Item Number"
                                />
                              ) : (
                                <span className="text-gray-600" title="External Item Number">
                                  {line.mserp_externalitemnumber || ''}
                                </span>
                              )}
                            </td>

                            <td className="px-1 py-2" style={{ width: '80px' }}>
                              {/* Flow field is read-only */}
                              <span className="text-gray-600" title="Flow (Read-only)">
                                {line.mserp_alg_flow || ''}
                              </span>
                            </td>

                            <td className="px-1 py-2" style={{ width: '80px' }}>
                              {/* Pressure field is read-only */}
                              <span className="text-gray-600" title="Pressure (Read-only)">
                                {line.mserp_alg_pressure || ''}
                              </span>
                            </td>

                            <td className="px-1 py-2" style={{ width: '90px' }}>
                              {/* Grade RPM field is read-only */}
                              <span className="text-gray-600" title="Grade RPM (Read-only)">
                                {line.mserp_alg_graderpm || ''}
                              </span>
                            </td>

                            <td className="px-1 py-2" style={{ width: '105px' }}>
                              {/* ALG_DefaultNetSalesPrice is always read-only */}
                              <span className="text-gray-600 italic" title="Read-only field">
                                {formatCurrency(line.mserp_alg_defaultnetsalesprice)}
                              </span>
                            </td>
                          </>
                        )}
                        
                        <td className="px-2 py-2" style={{ width: '130px' }}>
                          {isEditMode ? (
                            <input
                              type="number"
                              value={line.mserp_alg_netsalesprice ?? ''}
                              onChange={(e) => handleCellEdit(index, 'mserp_alg_netsalesprice', parseFloat(e.target.value) || 0)}
                              className={`w-full px-2 py-1 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary ${isFieldModified(index, 'mserp_alg_netsalesprice') ? 'text-brand-primary' : ''}`}
                              step="0.01"
                            />
                          ) : (
                            <span className={isFieldModified(index, 'mserp_alg_netsalesprice') ? 'text-brand-primary' : 'text-brand-darkText'}>
                              {formatCurrency(line.mserp_alg_netsalesprice)}
                            </span>
                          )}
                        </td>
                        
                        <td className="px-2 py-2" style={{ width: '130px' }}>
                          <span className={isFieldModified(index, 'mserp_lineamount') ? 'text-brand-primary' : 'text-brand-darkText'}>
                            {formatCurrency(line.mserp_lineamount)}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </>
          )}

          {!loading && (lines.length > 0 || editedLines.length > 0) && (
            <div className="mt-2 pt-3 border-t border-gray-300 flex-shrink-0">
              <div className="text-center text-sm text-brand-darkText">
                {t('quotation:lines.totalCount', { count: (isEditMode ? editedLines : lines).length })}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Grouping Modal */}
      <AnimatePresence>
        {showGroupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleCancelGrouping}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-96 max-w-90vw"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('quotation:lines.groupModal.title')}</h3>
                <button
                  onClick={handleCancelGrouping}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  {t('quotation:lines.groupModal.description')}
                </p>
                
                <div className="bg-gray-50 rounded p-3 mb-4 max-h-40 overflow-y-auto">
                  <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                    <span>{t('quotation:lines.groupModal.products')}</span>
                    <span>{t('quotation:lines.groupModal.quantity')}</span>
                  </div>
                  {Array.from(selectedLines).sort((a, b) => a - b).map(index => {
                    const line = editedLines[index]
                    return (
                      <div key={index} className="flex justify-between text-sm text-gray-600 py-1">
                        <span>{line?.mserp_itemnumber || 'N/A'}</span>
                        <span>{line?.mserp_requestedsalesquantity || 0}</span>
                      </div>
                    )
                  })}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('quotation:lines.groupModal.groupName')}
                  </label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('quotation:lines.groupModal.placeholder')}
                    autoFocus
                  />
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  {t('quotation:lines.groupModal.confirmQuestion')}
                </p>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleCancelGrouping}
                    className="px-4 py-2"
                  >
                    {t('common:actions.cancel')}
                  </Button>
                  <Button
                    variant="info"
                    onClick={handleConfirmGrouping}
                    disabled={!groupName.trim()}
                    className="px-4 py-2"
                  >
                    {t('common:actions.confirm')}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Send to ERP Confirmation Dialog */}
      {showSendToErpConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/50 p-4"
          onClick={() => setShowSendToErpConfirm(false)}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="send-to-erp-dialog-title"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="send-to-erp-dialog-title" className="text-lg font-semibold text-gray-900">
              {t('quotation:lines.confirmSendToErp')}
            </h3>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSendToErpConfirm(false)}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={confirmSendToFnO}
              >
                {t('common:actions.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ungroup Confirmation Modal */}
      <AnimatePresence>
        {showUngroupConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleCancelUngroup}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-96 max-w-90vw"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={handleCancelUngroup}
                  className="text-gray-400 hover:text-gray-600 ml-auto"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6 text-center">
                <p className="text-lg text-gray-800 mb-4">
                  {t('quotation:lines.ungroupModal.confirmMessage')}
                </p>

                <div className="flex justify-center space-x-4">
                  <Button
                    variant="danger"
                    onClick={handleConfirmUngroup}
                    className="px-6 py-2"
                  >
                    {t('common:actions.confirm')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancelUngroup}
                    className="px-6 py-2"
                  >
                    {t('common:actions.cancel')}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk LinePercent Edit Modal */}
      <AnimatePresence>
        {showLinePercentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleCancelBulkLinePercent}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-96 max-w-sm mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('quotation:lines.linePercentModal.title')}</h3>
                <button
                  onClick={handleCancelBulkLinePercent}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* Mode Selection Toggle */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('quotation:lines.linePercentModal.applyBy')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={!applyByLineDisc}
                      onChange={() => setApplyByLineDisc(false)}
                      className="mr-2"
                    />
                    <span className="text-sm">{t('quotation:lines.linePercentModal.selectedLines', { count: selectedLines.size })}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={applyByLineDisc}
                      onChange={() => setApplyByLineDisc(true)}
                      className="mr-2"
                    />
                    <span className="text-sm">{t('quotation:lines.linePercentModal.lineDiscValue')}</span>
                  </label>
                </div>
              </div>

              {/* Line Disc Dropdown (only shown when applyByLineDisc is true) */}
              {applyByLineDisc && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('quotation:lines.linePercentModal.selectLineDisc')}
                  </label>
                  <select
                    value={selectedLineDisc}
                    onChange={(e) => setSelectedLineDisc(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('quotation:lines.linePercentModal.selectPlaceholder')}</option>
                    <option value="__EMPTY__">{t('quotation:lines.linePercentModal.empty')}</option>
                    {getUniqueLineDiscValues().map(value => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('quotation:lines.linePercentModal.lineDiscHint')}
                  </p>
                </div>
              )}

              {/* LinePercent Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('quotation:lines.linePercentModal.linePercentLabel')}
                </label>
                <input
                  type="number"
                  value={bulkLinePercent}
                  onChange={(e) => setBulkLinePercent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('quotation:lines.linePercentModal.percentPlaceholder')}
                  step="0.01"
                  min="0"
                  max="100"
                  autoFocus={!applyByLineDisc}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {applyByLineDisc
                    ? selectedLineDisc === '__EMPTY__'
                      ? t('quotation:lines.linePercentModal.hintByLineDiscEmpty')
                      : t('quotation:lines.linePercentModal.hintByLineDisc')
                    : t('quotation:lines.linePercentModal.hintBySelection')}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={handleCancelBulkLinePercent}
                  className="px-4 py-2"
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  variant="info"
                  onClick={handleConfirmBulkLinePercent}
                  disabled={
                    !bulkLinePercent.trim() ||
                    isNaN(parseFloat(bulkLinePercent)) ||
                    (applyByLineDisc && !selectedLineDisc) ||
                    (!applyByLineDisc && selectedLines.size === 0)
                  }
                  className="px-4 py-2"
                >
                  {applyByLineDisc
                    ? selectedLineDisc === '__EMPTY__'
                      ? t('quotation:lines.linePercentModal.applyWithoutLineDisc')
                      : t('quotation:lines.linePercentModal.applyWithLineDisc', { value: selectedLineDisc })
                    : t('quotation:lines.linePercentModal.applyToLines', { count: selectedLines.size })}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Field Conflict Confirmation Modal */}
      <AnimatePresence>
        {showFieldConflictModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleCancelFieldConflict}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-96 max-w-90vw"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                {t('quotation:lines.fieldConflictModal.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {fieldConflictData.message}
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={handleCancelFieldConflict}
                  className="px-4 py-2"
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  variant="info"
                  onClick={handleConfirmFieldConflict}
                  className="px-4 py-2"
                >
                  {t('quotation:lines.fieldConflictModal.continue')}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )

  return createPortal(modalContent, document.body)
}
